<template>
  <div class="mb-5 overflow-y-visible">
    <div
      class="diagram-container position-relative"
      v-on:keydown.stop
    >
      <transition name="slide-in">
        <bpmn-side-panel
          v-show="showSidePanel"
          :node="eventsById[latestEventId]"
          :types-to-display-in-diagram="cardTypes"
          :cards="cardsByEventId[latestEventId]"
          :show-done="showDone"
          @add-card-ai="addCardAI"
          @ask-for-reply="onAskForReply"
          @update-card="onUpdateCard"
        />
      </transition>
      <template v-if="(!events || events?.length === 0) && !$store.state.isWorkflowDisabled && !creatingEvent">
        <div class="d-flex flex-row" :style="`position: absolute; z-index: 1; pointer-events: none; top: ${diagramYOffset}px;`">
          <v-btn
            variant="outlined"
            size="large"
            @click="addShape({parents: ['start']})"
            class="create-first-event-button ml-15 mt-14 mb-15"
            prepend-icon="mds:add-circle"
            style="pointer-events: auto;"
            :disabled="$store.state.isWorkflowDisabled"
          >
            Add start point
          </v-btn>
          <v-btn
            variant="outlined"
            size="large"
            class="generate-button ml-10 mt-14 mb-15 mr-10"
            @click="openAiGenerator()"
            prepend-icon="mds:temp_preferences_custom"
            style="pointer-events: auto;"
            :disabled="$store.state.isWorkflowDisabled"
          >
            Generate workflow with AI
          </v-btn>
          <v-btn
            variant="outlined"
            size="large"
            class="generate-button mt-14 mb-15 mr-5"
            @click="openWorkflowFromCodeDialog = true"
            prepend-icon="mdi-github"
            style="pointer-events: auto;"
            :disabled="$store.state.isWorkflowDisabled"
          >
            Generate workflow from code
          </v-btn>
        </div>
      </template>
      <div ref="canvas" class="canvas-container">
        <div ref="scrollContainer" class="scroll-container" :data-interaction-mode="$store.state.interactionMode">
          <svg
            v-show="events"
            :width="diagramWidth"
            :height="flowChartHeight"
            id="flowchart"
            ref="flowchart"
          >
            <foreignObject
              v-if="Object.keys(svgGroups).length > 0"
              :x="0"
              :y="0"
              :width="LANE_WIDTH"
              :height="GROUP_HEIGHT"
              class="group group-first"
            />
            <g v-for="(group) in svgGroups" :key="group.id">
              <foreignObject
                :key="group.id"
                :x="getXPointFromGroup(group)"
                :y="0"
                :width="group.width > 0 ? group.width : (diagramWidth - getXPointFromGroup(group))"
                height="24"
              >
                <div
                  style="position: relative;"
                  class="hover-container"
                  :data-test="group.name"
                >
                  <div
                    :ref="`groupName-${group.id}`"
                    :class="{
                      'group-last': group.width === -1
                    }"
                    class="group single-line d-flex align-center justify-center"
                    :id="group.id"
                    @keydown.stop="checkCharacterLimit($event, 40)"
                    @keydown.stop.enter="$event.target.blur()"
                    @keydown.stop.esc="restoreValue($event, group.name)"
                    @blur="updateGroupName($event, group)"
                    @paste="onPaste($event, $refs.groupName, 40, true)"
                    @click="closeInspectEvent()"
                    :contenteditable="!$store.state.isWorkflowDisabled"
                    v-text="group.name"
                    @input="() => updateFontSizeDynamically($refs[`groupName-${group.id}`][0], true)"
                  >
                  </div>
                  <span
                    v-if="!$store.state.isWorkflowDisabled"
                    class="delete-group-btn show-on-hover"
                    :style="`position: absolute; right: ${group.width === -1 ? LAST_GROUP_RIGHT_OFFSET : DEFAULT_RIGHT_OFFSET}px; top: ${GROUP_TOP_OFFSET}px;`"
                  >
                    <v-icon
                      class="delete-group-icon"
                      size="x-small"
                      icon="mds:delete"
                      @click="deleteGroup(group)"
                    />
                  </span>
                </div>
              </foreignObject>
            </g>

            <svg
              :y="diagramYOffset"
              :width="diagramWidth"
              :height="flowChartHeight"
              id="svg"
            >
              <rect
                v-if="!hasGroups"
                :x="4"
                :y="0.5"
                :width="diagramWidth + 1"
                :height="flowChartHeight - 1"
                fill="none"
                :style="{ stroke: 'var(--diagram-border)' }"
                class="main-rect"
              />
              <g
                v-for="(lane, name, index) in svgLanes"
                :key="'swimlane-' + lane.id"
                class="swime-lane-path"
                @click="closeInspectEvent()"
              >
                <path
                  :d="
                    computeSwimlanePath(
                      lane.x + 24,
                      lane.y,
                      diagramWidth,
                      lane.height,
                      index === 0
                    )
                  "
                  style="stroke: var(--diagram-lane-divider); stroke-width: 1px"
                ></path>
                <rect
                  :x="lane.x"
                  :y="lane.y"
                  :width="diagramWidth"
                  :height="lane.height"
                  :opacity="(index % 2 === 0) ? 0.0 : 0.01"
                  fill="black"
                  class="lane-rect"
                  :data-test="`lane-rect-${lane.name}`"
                  @contextmenu.prevent="showLaneContextMenu($event, lane, index)"
                />
                <template v-if="hasGroups">
                  <g
                    v-for="group in sortedGroups"
                    :key="`lane-cell-${lane.id}-${group.id}`"
                    class="lane-cell-group"
                  >
                    <foreignObject
                      :x="getXPointFromGroup(group) + LANE_CELL_MENU_OFFSET_X"
                      :y="lane.y + LANE_CELL_MENU_OFFSET_Y"
                      :width="LANE_CELL_MENU_SIZE"
                      :height="LANE_CELL_MENU_SIZE"
                      class="lane-cell-menu-container"
                    >
                      <div class="lane-cell-menu-wrapper">
                        <button
                          type="button"
                          :id="`lane-menu-button-${lane.name.split(' ').join('-')}`"
                          :data-test="`lane-menu-button-${lane.name.split(' ').join('-')}-${group.name.split(' ').join('-')}`"
                          class="lane-cell-menu-button"
                          :disabled="$store.state.isWorkflowDisabled"
                          @click.stop="showLaneCellMenu($event, lane, index, group)"
                        >
                          <v-icon size="16">mdi-dots-horizontal</v-icon>
                        </button>
                      </div>
                    </foreignObject>
                  </g>
                </template>
                <template v-else>
                  <g class="lane-cell-group">
                    <rect
                      :x="lane.x"
                      :y="lane.y"
                      :width="diagramWidth"
                      :height="lane.height"
                      class="lane-cell"
                      :data-test="`lane-rect-${lane.name}`"
                      pointer-events="none"
                    />
                    <foreignObject
                      :x="lane.x + LANE_CELL_MENU_OFFSET_X + 24"
                      :y="lane.y + LANE_CELL_MENU_OFFSET_Y"
                      :width="LANE_CELL_MENU_SIZE"
                      :height="LANE_CELL_MENU_SIZE"
                      class="lane-cell-menu-container"
                    >
                      <div class="lane-cell-menu-wrapper">
                        <button
                          type="button"
                          :id="`lane-menu-button-${lane.name.split(' ').join('-')}`"
                          :data-test="`lane-menu-button-${lane.name.split(' ').join('-')}`"
                          class="lane-cell-menu-button"
                          :disabled="$store.state.isWorkflowDisabled"
                          @click.stop="showLaneCellMenu($event, lane, index)"
                        >
                          <v-icon size="16">mdi-dots-horizontal</v-icon>
                        </button>
                      </div>
                    </foreignObject>
                  </g>
                </template>
              </g>

              <g v-for="group of Object.values(svgGroups).slice(1)" :key="group.id">
                <path
                  :d="`M ${group.x} 0 V 999999`"
                  style="stroke: var(--diagram-lane-divider); stroke-width: 1px;"
                ></path>
                <path
                  v-show="dragging && group.id === selectedElement?.id"
                  :d="`M ${groupDragX} 0 V 999999`"
                  style="stroke: var(--diagram-lane-divider); stroke-width: 2px;"
                ></path>
                <rect
                  :data-test="`group-divider-${group.name}`"
                  :id="group.id"
                  :x="group.x - 2" y="0"
                  width="3" height="999999"
                  :class="group.startSlotX === 0 ? 'notDraggable' : 'draggable horizontal'"
                  :fill="dragging && group.id === selectedElement?.id ? 'rgb(46, 156, 230)' : 'transparent'"
                  stroke="transparent"
                >
                </rect>
              </g>

              <template v-for="node in svgGraph">
                <!-- Pre Loop to print parts of the svg that has to be on the lower layer -->
                <!-- Event Path -->
                <template v-for="(parentId, indexParent) in node.parents" :key="node.id + indexParent + '80'">
                  <g v-if="parentId === 'start'">
                    <rect
                      :x="node.x - 26"
                      :y="node.y + 44"
                      width="8"
                      height="8"
                      :style="{ fill: 'var(--diagram-stroke)' }"
                      rx="2"
                    />
                    <line
                      :x1="node.x - 26"
                      :y1="node.y + 48"
                      :x2="node.x + (node.type === 'bpmn:ExclusiveGateway' ? 4.5 : 0)"
                      :y2="node.y + 48"
                      :style="{ stroke: 'var(--diagram-stroke)' }"
                      stroke-width="1"
                    />
                  </g>
                  <circle
                    v-if="svgGraph[parentId]"
                    :cx="svgGraph[parentId].x + (svgGraph[parentId].type === 'bpmn:ExclusiveGateway' ? 102 : 105)"
                    :cy="svgGraph[parentId].y + 96/2"
                    r="1" :style="{ fill: 'var(--diagram-stroke)' }"
                  />
                  <path
                    v-if="svgGraph[parentId]"
                    :d="createPathBetweenNodes(parentId, node.id)"
                    class="sequence-arrow"
                    :class="{
                      highlighted: isArrowHighlighted(parentId, node.id),
                      outlined:
                        node.outlined || svgGraph[node.parents[0]]?.outlined,
                    }"
                  ></path>
                  <!-- labels forward connections -->
                  <template
                    v-if="
                      svgGraph[parentId] && connectionHasLabel(parentId, node.id)
                    "
                  >
                    <!-- editable Event Label -->
                    <foreignObject
                      :key="node.id + indexParent + 'event-label'"
                      :x="svgGraph[parentId].x + 140"
                      :y="node.y + 10"
                      width="120"
                      height="32"
                    >
                      <bpmn-event-label
                        v-if="!node.outlined"
                        :node="node"
                        :conditionLabel="getConnectionLabel(node, parentId)"
                        @label-blur="
                          blurOnConditionLabels($event, node, parentId)
                        "
                        @label-focus="focusOnConditionLabel"
                      ></bpmn-event-label>
                    </foreignObject>
                  </template>
                </template>
              </template>
              <template v-for="node in svgGraph" :key="node.id">
                <!-- Event -->
                <!-- Event Box -->
                <template v-if="node.type == 'bpmn:Task'">
                  <bpmn-event
                    :key="node.id + '40'"
                    :node="node"
                    :svgGraph="svgGraph"
                    :svgGroups="svgGroups"
                    :svgLanes="svgLanes"
                    :conditionLabel="(svgGroups || [])[node.groupId]?.name || 'null'"
                    :eventDefinition="workflow.eventDefinition"
                    :workflow="workflow"
                    :selected-event-ids="selectedEventIds"
                    :connection-target="
                      enableConnectionTargets &&
                        connectionTargetsIds.includes(node.id)
                    "
                    class="bpmn-event-container"
                    @move-right="$emit('move-right')"
                    @move-left="$emit('move-left')"
                  ></bpmn-event>
                </template>

                <template v-else>
                  <bpmn-gateway
                    :key="node.id + '60'"
                    :node="node"
                    :svgGraph="svgGraph"
                    :svgLanes="svgLanes"
                    :svgGroups="svgGroups"
                    :workflow="workflow"
                    :selected-event-ids="selectedEventIds"
                    :connection-target="
                      enableConnectionTargets &&
                        connectionTargetsIds.includes(node.id)
                    "
                    :eventDefinition="workflow.eventDefinition"
                    @move-right="$emit('move-right')"
                    @move-left="$emit('move-left')"
                  ></bpmn-gateway>
                </template>

                <!-- button menu on selected event or exclusive gateway -->
                <!--
                as for vue v2.6.11:
                Slots do not render in foreignObjects if the foreignObject
                is not in the template of the same component that has
                the root svg element of the svg. Means you can't split svg
                into components properly.

                https://github.com/vuejs/vue/issues/11315
                https://github.com/vuejs/vue/pull/11349

              must be fixed in vue v2.6.13
              -->

                <!-- Add event button -->
                <foreignObject
                  v-if="!multiSelectionActive"
                  :x="node.x + (node.type === 'bpmn:ExclusiveGateway' ? 105 : 108)"
                  :y="node.y + 38"
                  width="40"
                  height="20"
                  class="add-event-button"
                  :class="{
                    'show-on-hover': $route.params.eventId !== node.id,
                  }"
                >
                  <v-btn
                    :disabled="$store.state.isWorkflowDisabled"
                    variant="flat"
                    size="16px"
                    class="captures-description"
                    :class="{
                      'opacity-100' : $route.params.eventId === node.id,
                    }"
                    @click="handleClickQuickAddEvent($event, node)"
                  >
                    <v-icon style="font-size: 16px;">mds:add</v-icon>
                  </v-btn>
                </foreignObject>

                <!-- colour badges on events and exclusive gateways -->
                <rect
                  v-if="node.type === 'bpmn:Task' || badgeDataByEvent[node.id]?.badges.length > 0"
                  :id="'rb-' + node.id"
                  :x="node.type === 'bpmn:Task' ? node.x : node.x + 28"
                  :y="node.y + 81"
                  :mask="`url(#rmask-${node.id})`"
                  fill="white"
                  opacity="0.3"
                  :width="node.type === 'bpmn:Task' ? 104 : 50"
                  height="15"
                />
                <foreignObject
                  :key="node.id + 'badge'"
                  v-if="cardTypes?.length > 0"
                  :x="node.x"
                  :y="node.y + 81"
                  :mask="`url(#rmask-${node.id})`"
                  width="104"
                  height="16"
                >
                  <div :class="`d-flex ${node.type === 'bpmn:ExclusiveGateway' ? 'justify-center' : 'justify-left'} align-left`">
                    <div
                      v-for="(badge, index) in (badgeDataByEvent[node.id]?.badges.slice(0, badgeDataByEvent[node.id]?.badges.length > 6 ? 5 : 6) || [])"
                      @click="inspectEvent(node.id)"
                      :key="badge.id"
                      :class="['card-badge', { 'd-none': badge.count <= 0, 'ml-05': index !== 0, 'flex-grow-1': badgeDataByEvent[node.id]?.badges.length === 6 }, getCardBgClass(badge)]"
                      :style="getCardBgClass(badge) ? undefined : { backgroundColor: getBackgroundColour(badge) }"
                    >
                      <span
                        class="card-badge-number"
                        :style="'color: ' + getTextColour(badge)"
                      >
                        {{ badge.count > 99 ? `${badge.count.toString()[0]}..` : badge.count }}
                      </span>
                    </div>

                    <div
                      v-if="$route.params.eventId === node.id && badgeDataByEvent[node.id]?.badges.length === 0 && node.type === 'bpmn:Task'"
                      style="background-color: var(--light-1);" class="card-badge"
                      @click="inspectEvent(node.id)"
                    >
                      <v-tooltip location="bottom">
                        <template v-slot:activator="{ props }">
                          <span v-bind="props" class="card-badge-number">+</span>
                        </template>
                        <span>Add cards using the sidebar</span>
                      </v-tooltip>
                    </div>

                    <div
                      v-if="!!badgeDataByEvent[node.id] && badgeDataByEvent[node.id].badges.length > 6"
                      class="card-badge card-more-badge"
                      @click="inspectEvent(node.id)"
                    >
                      <span class="card-badge-number">
                        +{{
                          badgeDataByEvent[node.id].restCardsCount > 99 ?
                            `${badgeDataByEvent[node.id].restCardsCount.toString()[0]}..` :
                            badgeDataByEvent[node.id].restCardsCount
                        }}
                      </span>
                    </div>
                  </div>
                </foreignObject>
              </template>

              <!-- definitions of arrow -->
              <defs>
                <marker
                  id="sequenceflow-end-white-black-1r79xjtcycpwpzz6dais2xasb"
                  viewBox="0 0 40 40"
                  refX="17"
                  refY="23"
                  markerWidth="40"
                  markerHeight="40"
                  orient="0"
                >
                  <path
                    d="M 22.208 22.226 C 22.807 22.622 22.807 23.5 22.208 23.896 L 19.773 25.502 C 19.108 25.941 18.223 25.464 18.223 24.667 L 18.223 21.455 C 18.223 20.658 19.108 20.181 19.773 20.62 L 22.208 22.226 Z"
                    style="stroke-width: 0 !important; fill: var(--diagram-stroke)"
                  />
                </marker>
              </defs>
            </svg>
          </svg>

          <div
            v-if="selectionRect"
            class="selection-box"
            data-test="selection-box"
            :style="selectionBoxStyle"
          ></div>
          <div
            v-else-if="selectionBoundingRect"
            class="selection-bounds"
            data-test="selection-bounds"
            :style="selectionBoundingRectStyle"
            @mousedown="handleSelectionBoundsMousedown"
          ></div>

          <!-- Error message tooltip -->
          <div
            v-if="invalidSelectionMessage && (selectionRect || selectionBoundingRect)"
            class="selection-error-message"
            data-test="selection-error-message"
            :style="selectionErrorMessageStyle"
          >
            {{ invalidSelectionMessage }}
          </div>

          <transition name="actions">
            <div v-if="showActions">
              <div
                v-if="
                  primarySelectedEvent &&
                    !$store.state.isWorkflowDisabled
                "
                :style="`
                position: absolute;
                left: ${getActionsMenuXPosition()}px;
                top: ${constrainActionsMenuYPosition()}px;
                transition: left ease 0.15s, top ease 0.15s, transform ease 0.15s;
                height: 21;
                ${selectionBoundingRect ? 'transform: translateX(-50%);' : ''}
              `"
                class="text-left"
              >
                <bpmn-diagram-actions
                  :selectedEvent="primarySelectedEvent"
                  :selectedEventIds="selectedEventIds"
                  :enableConnectionTargets="enableConnectionTargets"
                  :events="events"
                  :baseData="baseData"
                  :svgGraph="svgGraph"
                  :svgGroups="svgGroups"
                  :svgLanes="svgLanes"
                  :workflow="workflow"
                  :disable-single-selection-actions="multiSelectionActive"
                  :color="
                    primarySelectedEvent?.color
                      ? primarySelectedEvent.color.rgb
                      : workflow.eventDefinition.color.rgb
                  "
                  @on-copy="$emit('on-copy')"
                  @on-paste="$emit('on-paste')"
                  @on-cut="$emit('on-cut')"
                  @change-shape="showTransformOptions = !showTransformOptions"
                  @add-shape="addShape"
                  @add-shape-ai="addShapeAI"
                  @add-parallel-event="addParallelEvent"
                  @add-group="addGroup"
                  @clear-multi-selection="clearMultiSelection"
                />
              </div>
            </div>
          </transition>

          <!-- Shape Right is moved into BpmnDiagramActions above
          <div
            v-if="
              $route.params.eventId &&
              svgGraph?.[$route.params.eventId] &&
              !$store.state.isWorkflowDisabled
            "
            v-show="!dragging"
            :style="`
            position: absolute;
            left: ${svgGraph?.[$route.params.eventId]?.x + 105}px;
            top: ${svgGraph?.[$route.params.eventId]?.y - 1 + diagramYOffset}px;
            width: 22;
            height: 105;
          `"
          >
            <bpmn-diagram-actions-shape-right
              v-if="svgGraph[$route.params.eventId]"
              :selectedEvent="svgGraph[$route.params.eventId]"
              :baseData="baseData"
              @add-shape="addShape"
              :svgGraph="svgGraph"
              :svgGroups="svgGroups"
              :svgLanes="svgLanes"
            ></bpmn-diagram-actions-shape-right>
          </div>
          -->

          <!-- Subprocess icon -->
          <div
            v-for="node in subprocesses"
            :key="node.id + '-subprocess'"
            width="12"
            height="12"
            style="line-height: 1em; font-size: 0"
            class="subprocess-icon"
            :style="`
            position: absolute;
            left: ${svgGraph?.[node.id]?.x + 90}px;
            top: ${svgGraph?.[node.id]?.y + 2 + diagramYOffset}px;
            width: 12px;
            height: 12px;
          `"
          >
            <v-tooltip location="top" open-delay="500">
              <template v-slot:activator="{ on, props }">
                <router-link
                  :to="
                    '/workflow/' +
                      $route.params.projectId +
                      '/' +
                      node.subprocess.id
                  "
                  target="_blank"
                >
                  <v-icon v-bind="props" size="12" v-on="{ ...on }">custom:SubprocessIcon</v-icon>
                </router-link>
              </template>
              <span>Open subprocess: {{ node.subprocess.name }}</span>
            </v-tooltip>
          </div>
        </div>

        <div
          class="context-menu-top"
          v-if="svgGraph?.[$route.params.eventId]?.x && showTransformOptions"
          :style="`
            position: absolute;
            left: ${
            svgGraph?.[$route.params.eventId]?.x +
            56 -
            bpmnScrollLeft
          }px;
            top: ${svgGraph?.[$route.params.eventId]?.y - 48 - scrollTop + diagramYOffset}px;
            width: 70;
            height: 21;
          `"
          v-show="!dragging"
        >
          <bpmn-diagram-actions-shape-top-transform
            class="dark position-fixed"
            :selectedEvent="svgGraph[$route.params.eventId]"
            :baseData="baseData"
            :svgGraph="svgGraph"
            :svgGroups="svgGroups"
            :svgLanes="svgLanes"
            @transform-shape="hideTransformOptions"
            v-click-outside="closeTransformOptions()"
          />
        </div>
        <!-- Swimlane titles -->
        <div
          v-for="(lane, name, index) in svgLanes"
          :key="lane.id"
          :style="`
            width: 30px;
            position: absolute;
            left: ${lane.x}px;
            top: ${lane.y + diagramYOffset}px;
            height: ${lane.height}px;
          `"
        >
          <!-- editable Lane name (rotated) -->
          <div class="lane-title-container position-relative" style="z-index: 12;">
            <bpmn-swim-lane-title
              :first="index === 0 && !hasGroups"
              :last="index === noOfLanes - 1"
              :lane="lane"
              :index="index"
              :color="workflow.roleDefinition.color.rgb"
              :svgGraph="svgGraph"
              :svgLanes="svgLanes"
              :svgGroups="svgGroups"
              :workflow="workflow"
              @name-blur="blurOnRoleName"
            />

          </div>
        </div>
      </div>

      <!-- Context menu for lanes -->
      <v-menu
        ref="laneContextMenuRef"
        v-model="laneContextMenu.show"
        :location="laneContextMenu.location"
        :close-on-content-click="true"
        :transition="false"
      >
        <template #activator="{ props }">
          <div
            ref="laneContextMenuActivator"
            v-bind="props"
            style="position: fixed; width: 1px; height: 1px; z-index: -1;"
            :style="{
              left: (laneContextMenu.activator?.x || 0) + 'px',
              top: (laneContextMenu.activator?.y || 0) + 'px'
            }"
          ></div>
        </template>
        <v-list density="compact">
          <v-tooltip location="right">
            <template #activator="{ props: itemProps }">
              <div v-bind="itemProps">
                <v-list-item
                  :disabled="!clipboardHasValidJson"
                  prepend-icon="mdi-content-paste"
                  @click="clipboardHasValidJson && handleContextMenuAction('paste')"
                  data-test="paste-button"
                >
                  <v-list-item-title>Paste</v-list-item-title>
                </v-list-item>
              </div>
            </template>
            <span v-if="clipboardHasValidJson">
              Paste event(s) from clipboard
            </span>
            <span v-else>No valid event data in clipboard</span>
          </v-tooltip>

          <v-divider />

          <v-list-item
            @click="handleContextMenuAction('add-start-point')"
            data-test="add-start-point-button"
            prepend-icon="mds:add-circle"
          >
            <v-list-item-title>Add start point here</v-list-item-title>
          </v-list-item>

          <v-divider />

          <v-list-item
            @click="handleContextMenuAction('add-lane-above')"
            data-test="add-lane-above-button"
            prepend-icon="mdi-arrow-up"
          >
            <v-list-item-title>Add lane above</v-list-item-title>
          </v-list-item>
          <v-list-item
            @click="handleContextMenuAction('add-lane-below')"
            data-test="add-lane-below-button"
            prepend-icon="mdi-arrow-down"
          >
            <v-list-item-title>Add lane below</v-list-item-title>
          </v-list-item>
          <v-tooltip v-if="!isLaneEmpty(laneContextMenu.laneId)" location="right">
            <template #activator="{ props: tooltipProps }">
              <div v-bind="tooltipProps">
                <v-list-item :disabled="true" data-test="delete-role-button" prepend-icon="mds:delete">
                  <v-list-item-title>Delete lane</v-list-item-title>
                </v-list-item>
              </div>
            </template>
            <span>Cannot delete a lane that is not empty.</span>
          </v-tooltip>
          <v-list-item
            v-else
            @click="handleContextMenuAction('delete-lane')"
            data-test="delete-role-button"
            prepend-icon="mds:delete"
          >
            <v-list-item-title>Delete lane</v-list-item-title>
          </v-list-item>

          <v-divider />

          <v-list-item
            v-if="sortedGroups.length === 0"
            @click="handleContextMenuAction('add-group-before')"
            prepend-icon="mdi-plus"
          >
            <v-list-item-title>Add group</v-list-item-title>
          </v-list-item>
          <template v-else>
            <v-list-item
              @click="handleContextMenuAction('add-group-before')"
              prepend-icon="mdi-arrow-left"
            >
              <v-list-item-title>Add group before</v-list-item-title>
            </v-list-item>
            <v-list-item
              @click="handleContextMenuAction('add-group-after')"
              prepend-icon="mdi-arrow-right"
            >
              <v-list-item-title>Add group after</v-list-item-title>
            </v-list-item>
          </template>
          <v-tooltip
            v-if="!laneContextMenu.group"
            location="right"
          >
            <template #activator="{ props: tooltipProps }">
              <div v-bind="tooltipProps">
                <v-list-item :disabled="true" data-test="delete-group-button" prepend-icon="mds:delete">
                  <v-list-item-title>Delete group</v-list-item-title>
                </v-list-item>
              </div>
            </template>
            <span>No group to delete.</span>
          </v-tooltip>
          <v-list-item
            v-else
            data-test="delete-group-button"
            @click="handleContextMenuAction('delete-group')"
            prepend-icon="mds:delete"
          >
            <v-list-item-title>Delete group</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <bpmn-diagram-actions-bottom
        :selectedEvent="svgGraphData[$route.params.eventId]"
        :cardTypes="cardTypes"
        :base-data="baseData"
        :svgGraph="svgGraph"
        :svgGroups="svgGroups"
        :svgLanes="svgLanes"
        @add-shape="addShape"
        @add-group="addGroup"
        @add-lane="addRole"
      />
    </div>

    <bpmn-generator
      :bpm="thisComponent()"
      :workflow="workflow"
      :workflowTasks="events ? events : {}"
      ref="generator"
    />

    <workflow-from-code
      :modelValue="openWorkflowFromCodeDialog"
      @update:modelValue="(val) => openWorkflowFromCodeDialog = val"
    />
  </div>
</template>

<script>
import BpmnEvent from '@/components/BpmnEvent'
import BpmnGateway from '@/components/BpmnGateway'
import BpmnDiagramActions from '@/components/BpmnDiagramActions'
import BpmnDiagramActionsBottom from '@/components/BpmnDiagramActionsBottom'
import BpmnSwimLaneTitle from '@/components/BpmnSwimLaneTitle'
import BpmnSidePanel from '@/components/BpmnSidePanel'
import BpmnDiagramActionsShapeTopTransform from '@/components/BpmnDiagramActionsShapeTopTransform'
import BpmnEventLabel from '@/components/BpmnEventLabel'
// import BpmnEventLabelVertical from '@/components/BpmnEventLabelVertical'
import workflowApi from '@/api/workflowApi'
import { workflowApiHelpers } from '@/api/workflowApiHelpers'
import cardTypesApi from '@/api/cardTypesApi'
import { diagramHelpers } from '@/mixins/diagramHelpers.js'
import { EventBus } from '@/event-bus/event-bus.js'
import BpmnGenerator from '@/components/BpmnGenerator'
import WorkflowFromCode from '@/components/WorkflowFromCode.vue'
import { mapGetters, mapMutations, mapState } from 'vuex'
import randomUUID from '@/utils/uuid'
import { getColorName } from '@/utils/color'
import { scrollToEvent } from '@/utils/scroll'
import safeStructuredClone from '@/utils/clone'
import dragFunctions from '@/helpers/dragFunctions'
import positionHelpers from '@/helpers/positionHelpers'
import dataHelpers from '@/helpers/dataHelpers'
import svgPathHelpers from '@/helpers/svgPathHelpers'
import { updateFontSizeDynamically } from '@/utils/update-font'
import copyPasteHelpers from '@/helpers/copyPasteHelpers'
import cardHelpers from '@/helpers/cardHelpers'

export default {
  name: 'Bpmn',
  components: {
    WorkflowFromCode,
    BpmnEvent,
    BpmnEventLabel,
    BpmnGateway,
    BpmnDiagramActions,
    BpmnDiagramActionsBottom,
    BpmnSwimLaneTitle,
    BpmnSidePanel,
    BpmnDiagramActionsShapeTopTransform,
    BpmnGenerator
  },
  data () {
    return {
      aiGenerator: false,
      selectedElement: null,
      timeout: null,
      dragOffsetX: null,
      dragOffsetY: null,
      startDragX: null,
      startDragY: null,
      slotX: 0,
      watchInPause: false,
      svgGraph: null,
      svgLanes: null,
      cardsByEventId: {},
      svgGroups: {},
      // savingChanges: false,
      dragging: false,
      showActions: false,
      latestEventId: null,
      enableConnectionTargets: false,
      showTransformOptions: false,
      triggerUpdateFromDrag: false,
      autoscroll: null,
      clientY: null,
      scrollTop: null,
      scrollLeft: null,
      occupiedSlotsVerticalLines: {},
      creatingEvent: false,
      draggingCanvasStartX: 0,
      isDraggingCanvas: false,
      isSelecting: false,
      selectionStart: null,
      selectionRect: null,
      selectionEvents: [],
      selectedEventIds: [],
      isSelectionConnected: true,
      selectionBoundingRect: null,
      invalidSelectionMessage: null,
      isSelectionDragging: false,
      selectionDragStart: null,
      selectionDragInitialRect: null,
      selectionDragInitialNodes: [],
      multiSelectionSlotX: 0,
      multiSelectionInputSlotX: 0,
      multiSelectionOutputSlotX: 0,
      multiSelectionInputAnchorId: null,
      multiSelectionOutputAnchorId: null,
      justFinishedDragging: false,
      justFinishedSelecting: false,
      openWorkflowFromCodeDialog: false,
      laneContextMenu: {
        show: false,
        location: 'bottom',
        activator: null,
        laneId: null,
        laneIndex: null,
        group: null
      },
      clipboardHasValidJson: false,
      LANE_WIDTH: 24,
      GROUP_HEIGHT: 24,
      LAST_GROUP_RIGHT_OFFSET: 18,
      DEFAULT_RIGHT_OFFSET: 2,
      GROUP_TOP_OFFSET: 3,
      LANE_CELL_MENU_OFFSET_X: 6,
      LANE_CELL_MENU_OFFSET_Y: 6,
      LANE_CELL_MENU_SIZE: 24
    }
  },
  props: [
    'showDone'
  ],
  mixins: [diagramHelpers],
  computed: {
    ...mapGetters({
      baseData: 'svgGraphData/baseData',
      svgLanesData: 'svgGraphData/svgLanesData',
      svgGraphData: 'svgGraphData/svgGraphData',
      svgGroupsData: 'svgGraphData/svgGroupsData',
      diagramWidth: 'synchronizedScrolling/diagramWidth',
      loadingWorkflowUpdate: 'workflow/getLoadingUpdate'
    }),
    ...mapState({
      workflow: state => state.workflow.workflow,
      cardTypes: state => state.cardTypes.cardTypes,
      events: (state) => state.workflow.workflow.domainEvents || [],
      showReleaseNo: state => state.workflow.showReleaseNo,
      bpmnScrollLeft: state => state.synchronizedScrolling.bpmnScrollLeft,
      sidePanelTab: state => state.sidePanelTab,
      showSidePanel: state => state.showSidePanel,
      hasClosedSidePanel: state => state.hasClosedSidePanel,
      svgContainerWidth: state => state.synchronizedScrolling.svgContainerWidth
    }),
    hasGroups () {
      return Object.keys(this.svgGroups)?.length > 0
    },
    diagramYOffset () {
      return this.hasGroups ? 24 : 0
    },
    flowChartHeight () {
      let res = this.diagramYOffset
      for (const lane in this.svgLanes) {
        res = res + this.svgLanes[lane].height
      }
      if (Object.values(this.svgLanes || {}).length === 0) res = 156
      return res
    },
    noOfLanes () {
      return Object.keys(this.svgLanes).length
    },
    eventsById () {
      if (!this.events) { return {} }
      return this.events.reduce((accum, curr) => {
        accum[curr.id] = curr
        return accum
      }, {})
    },
    listValidTargetsForSequenceArrows () {
      const { eventId } = this.$route.params

      if (!eventId || !Array.isArray(this.events)) {
        return []
      }

      return this.events.filter((wt) =>
        (wt.id !== eventId) &&
        (!wt.parents || !wt.parents.find(parent => parent === eventId))
      )
    },
    connectionTargetsIds () {
      return this.listValidTargetsForSequenceArrows.map(({ id }) => id)
    },
    subprocesses () {
      return this.events ? this.events.filter(event => event.subprocess) : []
    },
    groupDragX () {
      return positionHelpers.getGroupXPointFromSlot(this.slotX)
    },
    slotToGroupMap () {
      const groups = Object.values(this.svgGroups)
      if (groups?.length > 0) {
        return dataHelpers.slotToGroupMap(groups, this.baseData.columns.length)
      } else {
        return {}
      }
    },
    badgeDataByEvent () {
      const badgeByEvent = {}
      Object.values(this.svgGraph).forEach(node => {
        badgeByEvent[node.id] = this.computeEventBadgesData(node)
      })
      return badgeByEvent
    },
    primarySelectedEventId () {
      if (Array.isArray(this.selectedEventIds) && this.selectedEventIds.length > 0) {
        return this.selectedEventIds[0]
      }
      return this.$route.params.eventId || null
    },
    primarySelectedEvent () {
      const id = this.primarySelectedEventId
      if (!id) {
        return null
      }
      return this.svgGraph?.[id] || null
    },
    multiSelectionActive () {
      return Array.isArray(this.selectedEventIds) && this.selectedEventIds.length > 1
    },
    selectionBoxStyle () {
      if (!this.selectionRect) {
        return {}
      }
      const { x, y, width, height } = this.selectionRect
      const isValid = this.isSelectionConnected
      const borderColor = isValid ? '#2e9ce6' : '#e63946'
      const bgColor = isValid ? 'rgba(46, 156, 230, 0.15)' : 'rgba(230, 57, 70, 0.15)'
      return {
        left: `${x}px`,
        top: `${y}px`,
        width: `${width}px`,
        height: `${height}px`,
        borderColor,
        backgroundColor: bgColor
      }
    },
    selectionBoundingRectStyle () {
      if (!this.selectionBoundingRect) {
        return {}
      }
      const { x, y, width, height } = this.selectionBoundingRect
      const isValid = this.isSelectionConnected
      const borderColor = isValid ? '#2e9ce6' : '#e63946'
      const borderStyle = isValid ? 'solid' : 'dashed'
      const bgColor = isValid ? 'rgba(46, 156, 230, 0.06)' : 'rgba(230, 57, 70, 0.15)'
      return {
        left: `${x}px`,
        top: `${y}px`,
        width: `${width}px`,
        height: `${height}px`,
        border: `1px ${borderStyle} ${borderColor}`,
        backgroundColor: bgColor,
        pointerEvents: isValid ? 'auto' : 'none',
        cursor: isValid ? 'move' : 'default'
      }
    },
    selectionErrorMessageStyle () {
      const container = this.$refs.scrollContainer
      if (!container) {
        return { display: 'none' }
      }

      const rect = this.selectionRect || this.selectionBoundingRect
      if (!rect) {
        return { display: 'none' }
      }

      const containerRect = container.getBoundingClientRect()

      // Calculate fixed position relative to viewport
      // rect.x and rect.y are relative to the scroll container's content
      const centerX = containerRect.left + rect.x - container.scrollLeft + (rect.width / 2)
      const topY = containerRect.top + rect.y - container.scrollTop - 28

      return {
        left: `${centerX}px`,
        top: `${topY}px`,
        transform: 'translateX(-50%)'
      }
    },
    isGrabbable () {
      return this.svgContainerWidth < this.diagramWidth
    },
    sortedGroups () {
      if (!this.hasGroups) {
        return []
      }

      return Object.values(this.svgGroups).sort((a, b) => a.startSlotX - b.startSlotX)
    }
  },
  methods: {
    updateFontSizeDynamically,
    ...mapMutations({
      setBpmnScrollLeft: 'synchronizedScrolling/setBpmnScrollLeft',
      setSvgContainerWidth: 'synchronizedScrolling/setSvgContainerWidth'
    }),
    handleClickQuickAddEvent (event, node) {
      // blur the button to avoid focus issues
      document.activeElement.blur()
      if (this.$store.state.isWorkflowDisabled) {
        return
      }
      this.addShape({ parents: [node.id] }, { parentEventId: node.id })
    },
    isArrowHighlighted (parentId, childId) {
      if (this.dragging) {
        return false
      }
      let xSlot
      const parentNode = this.svgGraph[parentId]
      const childNode = this.svgGraph[childId]
      // only highlight arrows around the selected event
      if ([parentId, childId].includes(this.$route.params.eventId)) {
        if (parentNode.type === 'bpmn:ExclusiveGateway') {
          // the vertical line after a decision
          // is positioned directly after the desicion
          xSlot = positionHelpers.getSlotXFromPoint(parentNode.x)
        } else {
          // the vertical line after an event
          // is positioned directly before the child
          xSlot = positionHelpers.getSlotXFromPoint(childNode.x) - 1
        }
        let ySlotStart = positionHelpers.getSlotYFromPoint(parentNode.y)
        const ySlotEnd = positionHelpers.getSlotYFromPoint(childNode.y)
        const backwardsConnection = parentNode.x > childNode.x
        if (backwardsConnection) {
          // if the parent is above the child
          // the vertical line still approaches the child from one step below
          if (ySlotEnd > ySlotStart) {
            ySlotStart = ySlotEnd + 1
          }
          if (this.isColliding(xSlot, ySlotStart, ySlotEnd, backwardsConnection)) {
            return true
          }

          // the parent has a downwards arrow that also needs to be
          // checked for collisions
          xSlot = positionHelpers.getSlotXFromPoint(parentNode.x)
          ySlotStart = positionHelpers.getSlotYFromPoint(parentNode.y)
          if (this.isColliding(xSlot, ySlotStart, ySlotStart + 1, backwardsConnection)) {
            return true
          }
        } else {
          // forward connection
          if (ySlotStart !== ySlotEnd) {
            if (this.isColliding(xSlot, ySlotStart, ySlotEnd, backwardsConnection)) {
              return true
            }
          }
        }
      }
      return false
    },
    getXPointFromGroup (group) {
      return positionHelpers.getGroupXPointFromSlot(group.startSlotX)
    },
    getGroupWidth (group) {
      if (!group) {
        return 0
      }
      if (group.width && group.width > 0) {
        return group.width
      }
      return Math.max(this.diagramWidth - this.getXPointFromGroup(group), 0)
    },
    showLaneCellMenu (event, lane, index, group) {
      this.showLaneContextMenu(event, lane, index, group)
    },
    inspectEvent (eventId, tab = 'cards') {
      this.changeSelectedEvent(eventId)
      this.latestEventId = eventId
      this.$store.commit('setSidePanelTab', tab)
      this.$store.commit('showSidePanel', true)
    },
    closeInspectEvent (event) {
      if (this.justFinishedSelecting) {
        this.justFinishedSelecting = false
        return
      }
      if (!event || !event.shiftKey) {
        this.clearMultiSelection()
      }
      this.changeSelectedEvent(null)
      this.latestEventId = null
    },
    handleShiftClickSelection (eventId) {
      if (!eventId) return

      // Start with current selection or active event
      const currentlySelected = this.selectedEventIds.length > 0
        ? [...this.selectedEventIds]
        : (this.$route.params.eventId ? [this.$route.params.eventId] : [])

      // Toggle event in selection
      const index = currentlySelected.indexOf(eventId)
      if (index > -1) {
        currentlySelected.splice(index, 1)
      } else {
        currentlySelected.push(eventId)
      }

      // Update based on selection size
      if (currentlySelected.length <= 1) {
        this.clearMultiSelection()
        this.changeSelectedEvent(currentlySelected[0] || null)
      } else {
        this.selectedEventIds = currentlySelected
        this.selectionEvents = currentlySelected
        this.isSelectionConnected = this.checkSelectionConnectivity(currentlySelected)
        this.updateSelectionBoundingRect()
        this.updateShowActions()

        // Navigate to first selected event if needed
        if (this.$route.params.eventId !== currentlySelected[0]) {
          this.$router.push({
            path: `/workflow/${this.$route.params.projectId}/${this.$route.params.id}/${currentlySelected[0]}`
          }).catch(() => {})
        }
      }
    },
    openAiGenerator () {
      this.$refs.generator.openSettings()
    },
    onAskForReply (event) {
      this.$refs.generator.addReplyToDiagram(event)
    },
    onUpdateCard (card) {
      const payload = {
        cardId: card.id,
        sortkey: card.sortkey,
        releaseNo: card.releaseNo,
        cardTypeId: card.cardTypeId,
        updateNote: {
          action: 'moved card',
          target: cardHelpers.getCardTarget(card)
        }
      }

      if ('tags' in card) {
        payload.tags = card.tags
      }
      if ('description' in card) {
        payload.description = card.description
      }

      workflowApi.updateCardsMutation(this.$route.params.id, [payload])
    },
    thisComponent () {
      return this
    },
    async checkClipboardValidity () {
      this.clipboardHasValidJson = await copyPasteHelpers.checkClipboardValidity()
    },
    async showLaneContextMenu (event, lane, index, groupOverride = null) {
      if (this.$store.state.isWorkflowDisabled) {
        return
      }

      // Check clipboard validity for paste option
      await this.checkClipboardValidity()

      // Determine which group the click is in
      const clickX = event.clientX + this.$refs.scrollContainer.scrollLeft - 90
      const group = groupOverride || positionHelpers.getGroupFromXCoordinate(clickX, this.svgGroups)

      // Store the old show state
      const wasOpen = this.laneContextMenu.show

      // Update the context menu position and data
      this.laneContextMenu.activator = { x: event.clientX, y: event.clientY }
      this.laneContextMenu.laneId = lane.id
      this.laneContextMenu.laneIndex = index
      this.laneContextMenu.group = group || null

      // If menu is already open, update position with minimal flicker
      if (wasOpen) {
        // Close and reopen instantly using requestAnimationFrame for smooth transition
        // The :transition="false" on v-menu prevents animation flicker
        this.laneContextMenu.show = false
        // Use requestAnimationFrame to ensure the close happens in the next frame
        requestAnimationFrame(() => {
          // Reopen immediately in the same frame cycle
          this.laneContextMenu.show = true
          this.$nextTick(() => {
            if (this.$refs.laneContextMenuActivator) {
              this.$refs.laneContextMenuActivator.click()
            }
          })
        })
        return
      }

      // Trigger menu in nextTick to ensure activator is positioned
      this.$nextTick(() => {
        this.laneContextMenu.show = true
        // Programmatically click the activator to trigger the menu
        if (this.$refs.laneContextMenuActivator) {
          this.$refs.laneContextMenuActivator.click()
        }
      })
    },
    handleContextMenuAction (action, groupId = null) {
      const { laneId, laneIndex, group } = this.laneContextMenu
      this.laneContextMenu.show = false

      switch (action) {
        case 'paste':
          // Pass the target laneId and groupId from context menu
          this.$emit('on-paste', {
            targetLaneId: laneId,
            targetGroupId: group?.id || null
          })
          break
        case 'add-start-point':
          this.addStartingPointToLaneAndGroup(laneId, group?.id || null)
          break
        case 'add-lane-above':
          this.addLaneRelativeTo(laneId, 'above')
          break
        case 'add-lane-below':
          this.addLaneRelativeTo(laneId, 'below')
          break
        case 'add-group-before':
          if (group) {
            this.addIntermediateGroup(group, 'before')
          } else {
            this.addGroup()
          }
          break
        case 'add-group-after':
          if (group) {
            this.addIntermediateGroup(group, 'after')
          } else {
            this.addGroup()
          }
          break
        case 'delete-group':
          if (group) {
            this.deleteGroup(group)
          }
          break
        case 'delete-lane':
          this.deleteLane(laneId, laneIndex)
          break
      }
    },
    addStartingPointToLaneAndGroup (laneId, groupId) {
      this.addShape(
        {
          laneId,
          groupId: (!groupId || !this.sortedGroups.length || groupId === this.sortedGroups[0].id)
            ? null // null is for first group
            : groupId
        },
        { parentEventId: 'start' }
      )
    },
    addStartingPointToLane (laneId) {
      const groupId = this.sortedGroups.length > 0 ? this.sortedGroups[0].id : null
      this.addStartingPointToLaneAndGroup(laneId, groupId)
    },
    resetDiagram () {
      this.endDrag(false)
      this.svgGraph = JSON.parse(JSON.stringify(this.svgGraphData))
      this.svgLanes = JSON.parse(JSON.stringify(this.svgLanesData))
      this.svgGroups = JSON.parse(JSON.stringify(this.svgGroupsData))

      this.$store.commit('updateSvgGraph', JSON.parse(JSON.stringify(this.svgGraphData)))
      this.$store.commit('updateSvgLanes', JSON.parse(JSON.stringify(this.svgLanesData)))
    },
    computeEventBadgesData (node) {
      const badges = []

      for (const cardType of this.cardTypes) {
        const cardsToShow = node.cards?.filter((req) => {
          return !req.done &&
            req.cardType &&
            req.cardType.id === cardType.id &&
            (!this.showReleaseNo || this.showReleaseNo?.length === 0 || this.showReleaseNo?.includes(req.releaseNo))
        })

        const count = cardsToShow?.length || 0

        if (count > 0) {
          badges.push({ ...cardType, count })
        }
      }

      return { badges, restCardsCount: badges.slice(5).reduce((a, b) => a + b.count, 0) }
    },
    computeCardsByEventId () {
      if (!this.events) { return {} }
      return this.events.reduce((accum, event) => {
        accum[event.id] = event.cards?.filter((req) =>
          !this.showReleaseNo ||
          this.showReleaseNo.length === 0 ||
          this.showReleaseNo.includes(req.releaseNo)
        ) || []
        return accum
      }, {})
    },
    updateShowActions () {
      const canShow = !!this.primarySelectedEvent &&
        !this.$store.state.isWorkflowDisabled &&
        !this.dragging &&
        !this.isSelectionDragging &&
        !this.isSelecting
      this.showActions = canShow
    },
    startSelection (event) {
      const container = this.$refs.scrollContainer
      if (!container) {
        return
      }
      const rect = container.getBoundingClientRect()
      const startX = event.clientX - rect.left + container.scrollLeft
      const startY = event.clientY - rect.top + container.scrollTop
      this.isSelecting = true
      this.selectionEvents = []
      this.selectedEventIds = []
      this.selectionBoundingRect = null
      this.invalidSelectionMessage = null
      this.isSelectionConnected = true
      this.selectionStart = { x: startX, y: startY }
      this.selectionRect = {
        x: startX,
        y: startY,
        width: 0,
        height: 0
      }

      // Disable text selection during multi-selection drag
      document.body.style.userSelect = 'none'

      this.updateShowActions()
    },
    updateSelection (event) {
      if (!this.isSelecting || !this.selectionStart) {
        return
      }
      const container = this.$refs.scrollContainer
      if (!container) {
        return
      }
      const rect = container.getBoundingClientRect()
      const currentX = event.clientX - rect.left + container.scrollLeft
      const currentY = event.clientY - rect.top + container.scrollTop
      const left = Math.min(currentX, this.selectionStart.x)
      const top = Math.min(currentY, this.selectionStart.y)
      const width = Math.abs(currentX - this.selectionStart.x)
      const height = Math.abs(currentY - this.selectionStart.y)
      this.selectionRect = { x: left, y: top, width, height }
      this.calculateSelectionEvents()
    },
    finishSelection () {
      if (this.isSelecting) {
        this.isSelecting = false
      }

      // Re-enable text selection
      document.body.style.userSelect = ''

      const hadSelectionRect = !!this.selectionRect
      if (this.selectionRect && this.selectionRect.width < 2 && this.selectionRect.height < 2) {
        this.selectionRect = null
        this.selectionEvents = []
      } else if (this.selectionRect) {
        this.calculateSelectionEvents(true)
      } else {
        this.selectionEvents = []
      }
      this.selectionStart = null
      if (hadSelectionRect) {
        this.selectionRect = null
      }
      if (this.selectionEvents.length > 0) {
        this.selectedEventIds = [...this.selectionEvents]

        // Set flag to prevent click handler from immediately clearing selection
        this.justFinishedSelecting = true
      } else if (this.$route.params.eventId) {
        this.selectedEventIds = [this.$route.params.eventId]
      } else {
        this.selectedEventIds = []
      }
      this.updateSelectionBoundingRect()
      this.updateShowActions()
    },
    calculateSelectionEvents (isFinal = false) {
      if (!this.selectionRect) {
        if (this.selectionEvents.length > 0) {
          this.selectionEvents = []
          // Clear selectedEventIds during dragging for real-time visual feedback
          if (this.isSelecting) {
            this.selectedEventIds = []
          }
        }
        return
      }
      const container = this.$refs.scrollContainer
      const flowchart = this.$refs.flowchart
      if (!container || !flowchart) {
        return
      }
      const containerRect = container.getBoundingClientRect()
      const selectedIds = []
      Object.values(this.svgGraph || {}).forEach(node => {
        const nodeElement = document.getElementById(`r-${node.id}`) ||
          document.getElementById(`p-${node.id}`) ||
          document.getElementById(node.id)
        if (!nodeElement) {
          return
        }
        const nodeRect = nodeElement.getBoundingClientRect()
        const rect = {
          x: nodeRect.left - containerRect.left + container.scrollLeft,
          y: nodeRect.top - containerRect.top + container.scrollTop,
          width: nodeRect.width,
          height: nodeRect.height
        }
        if (this.rectanglesOverlap(this.selectionRect, rect)) {
          selectedIds.push(node.id)
        }
      })
      const uniqueSelected = [...new Set(selectedIds)]

      let changed = uniqueSelected.length !== this.selectionEvents.length
      if (!changed) {
        const previousSet = new Set(this.selectionEvents)
        changed = uniqueSelected.some(id => !previousSet.has(id))
      }

      if (changed) {
        this.selectionEvents = uniqueSelected
        // Update selectedEventIds during dragging for real-time visual feedback
        this.selectedEventIds = [...uniqueSelected]
        // Check connectivity during dragging to update visual feedback
        this.isSelectionConnected = this.checkSelectionConnectivity(uniqueSelected)
        if (uniqueSelected.length > 0 && isFinal) {
          this.evaluateSelectionConnectivity()
        }
      } else if (isFinal) {
        if (uniqueSelected.length > 0) {
          this.evaluateSelectionConnectivity()
          this.updateSelectionBoundingRect()
        } else {
          this.selectionBoundingRect = null
        }
      }
    },
    checkSelectionConnectivity (selectedIds) {
      if (!selectedIds || selectedIds.length <= 1) {
        this.invalidSelectionMessage = null
        return true
      }

      // First check: all events must be connected to each other (single connected component)
      const selectedSet = new Set(selectedIds)
      const adjacency = this.buildSelectionAdjacency(selectedSet, this.svgGraph)

      if (adjacency.size === 0) {
        this.invalidSelectionMessage = 'Events/decisions must be connected'
        return false
      }

      // Use DFS to check if all selected events form a single connected component
      const visited = new Set()
      const traverse = (id) => {
        visited.add(id)
        const neighbors = adjacency.get(id) || []
        neighbors.forEach(neighbor => {
          if (!visited.has(neighbor)) {
            traverse(neighbor)
          }
        })
      }

      // Start traversal from the first event
      const firstId = Array.from(selectedSet)[0]
      traverse(firstId)

      // If not all events are visited, they're not all connected
      if (visited.size !== selectedSet.size) {
        this.invalidSelectionMessage = 'Shapes must be connected'
        return false
      }

      // Second check: all events must be in the same group
      const groups = new Set()
      for (const id of selectedIds) {
        const node = this.svgGraph[id]
        if (node) {
          const group = workflowApiHelpers.getGroupFromNode(node, this.svgGroups, this.svgGraph)
          // Use group.id to identify the group, or null if no group exists
          groups.add(group?.id || null)
        }
      }

      if (groups.size > 1) {
        this.invalidSelectionMessage = 'Events/decisions must be in the same group'
        return false
      }

      // Third check: input and output anchors must be different nodes
      const { inputAnchor, outputAnchor } = dragFunctions.getMultiSelectionAnchors(selectedIds, this.svgGraph)
      if (inputAnchor && outputAnchor && inputAnchor.id === outputAnchor.id) {
        this.invalidSelectionMessage = 'Selection has same input and output anchor'
        return false
      }

      // Fourth check: must have at most 1 external input and 1 external output,
      // external parent must not be a fork, and external child must not be a merge
      const isValid = this.canSafelyDeleteMultiSelectedEvents(selectedIds, this.workflow?.domainEvents || [], this.svgGraph)
      if (!isValid) {
        this.invalidSelectionMessage = 'Selection has multiple input/output connections'
        return false
      }

      this.invalidSelectionMessage = null
      return true
    },
    evaluateSelectionConnectivity () {
      if (!this.selectionEvents?.length) {
        return
      }
      const selectedSet = new Set(this.selectionEvents)
      const adjacency = this.buildSelectionAdjacency(selectedSet, this.svgGraph)
      if (adjacency.size === 0) {
        return
      }
      const components = []
      const visited = new Set()
      const traverse = (id, component) => {
        visited.add(id)
        component.push(id)
        const neighbors = adjacency.get(id) || []
        neighbors.forEach(neighbor => {
          if (!visited.has(neighbor)) {
            traverse(neighbor, component)
          }
        })
      }
      adjacency.forEach((_, id) => {
        if (!visited.has(id)) {
          const component = []
          traverse(id, component)
          components.push(component)
        }
      })
      if (components.length <= 1 && components[0]?.length === this.selectionEvents.length) {
        const connectedIds = [...selectedSet]

        // Check if all events are in the same group
        const groups = new Set()
        for (const id of connectedIds) {
          const node = this.svgGraph[id]
          if (node) {
            const group = workflowApiHelpers.getGroupFromNode(node, this.svgGroups, this.svgGraph)
            // Use group.id to identify the group, or null if no group exists
            groups.add(group?.id || null)
          }
        }

        if (groups.size > 1) {
          // Multiple groups - clear immediately
          this.selectionEvents = []
          this.selectedEventIds = []
          this.selectionBoundingRect = null
          this.invalidSelectionMessage = null
          this.updateShowActions()
          return
        }

        // Check if input/output connections are valid
        const { inputAnchor, outputAnchor } = dragFunctions.getMultiSelectionAnchors(connectedIds, this.svgGraph)
        const hasValidConnections = (!inputAnchor || !outputAnchor || inputAnchor.id !== outputAnchor.id) &&
          this.canSafelyDeleteMultiSelectedEvents(connectedIds, this.workflow?.domainEvents || [], this.svgGraph)
        if (hasValidConnections) {
          this.selectionEvents = connectedIds
          this.selectedEventIds = connectedIds
          this.invalidSelectionMessage = null
          this.updateSelectionBoundingRect()
          this.updateShowActions()
          return
        }
        // Connected but invalid input/output - clear immediately
        this.selectionEvents = []
        this.selectedEventIds = []
        this.selectionBoundingRect = null
        this.invalidSelectionMessage = null
        this.updateShowActions()
        return
      }
      // Not connected - clear immediately
      this.selectionEvents = []
      this.selectedEventIds = []
      this.selectionBoundingRect = null
      this.invalidSelectionMessage = null
      this.updateShowActions()
    },
    clearMultiSelection () {
      // Don't clear if we just finished dragging a multi-selection
      if (this.justFinishedDragging && this.selectedEventIds?.length > 1) {
        this.justFinishedDragging = false
        return
      }

      this.selectionEvents = []
      this.selectedEventIds = []
      this.selectionBoundingRect = null
      this.selectionRect = null
      this.isSelectionConnected = true
      this.invalidSelectionMessage = null
      this.updateShowActions()
    },
    updateSelectionBoundingRect () {
      if (this.isSelectionDragging) {
        return
      }
      if (!Array.isArray(this.selectedEventIds) || this.selectedEventIds.length <= 1) {
        this.selectionBoundingRect = null
        return
      }
      this.$nextTick(() => {
        const boundingRect = this.computeSelectionBoundingRect(this.selectedEventIds)
        this.selectionBoundingRect = boundingRect
      })
    },
    computeSelectionBoundingRect (ids) {
      const container = this.$refs.scrollContainer
      if (!container || !Array.isArray(ids) || ids.length <= 1) {
        return null
      }
      const containerRect = container.getBoundingClientRect()
      let minX = Number.POSITIVE_INFINITY
      let minY = Number.POSITIVE_INFINITY
      let maxX = Number.NEGATIVE_INFINITY
      let maxY = Number.NEGATIVE_INFINITY
      ids.forEach(id => {
        const nodeElement = document.getElementById(`r-${id}`) ||
          document.getElementById(`p-${id}`) ||
          document.getElementById(id)
        if (!nodeElement) {
          return
        }
        const nodeRect = nodeElement.getBoundingClientRect()
        const x = nodeRect.left - containerRect.left + container.scrollLeft
        const y = nodeRect.top - containerRect.top + container.scrollTop
        const right = x + nodeRect.width
        const bottom = y + nodeRect.height
        if (x < minX) minX = x
        if (y < minY) minY = y
        if (right > maxX) maxX = right
        if (bottom > maxY) maxY = bottom
      })
      if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
        return null
      }
      return {
        x: minX,
        y: minY,
        width: Math.max(0, maxX - minX),
        height: Math.max(0, maxY - minY)
      }
    },
    handleSelectionBoundsMousedown (event) {
      // If shift key is pressed, we want to allow shift+click on underlying elements
      if (event.shiftKey) {
        // Temporarily hide the overlay to find the element underneath
        const overlay = event.target
        const originalPointerEvents = overlay.style.pointerEvents
        overlay.style.pointerEvents = 'none'

        // Find the element at the mouse position
        const elementBelow = document.elementFromPoint(event.clientX, event.clientY)

        // Restore pointer events
        overlay.style.pointerEvents = originalPointerEvents

        // If we found an element, dispatch a click event to it with shift key
        if (elementBelow) {
          const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
            shiftKey: true,
            clientX: event.clientX,
            clientY: event.clientY,
            button: event.button
          })
          elementBelow.dispatchEvent(clickEvent)
        }

        event.preventDefault()
        event.stopPropagation()
        return
      }

      // Otherwise, start the selection drag
      this.startSelectionDrag(event)
    },
    startSelectionDrag (event) {
      if (event?.button !== 0 || this.isSelectionDragging || !this.selectedEventIds.length || !this.selectionBoundingRect || this.invalidSelectionMessage) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      this.isSelectionDragging = true
      this.dragging = true
      this.disableNormalScroll()
      this.selectionDragStart = { x: event.clientX, y: event.clientY }
      this.selectionDragInitialRect = { ...this.selectionBoundingRect }
      this.selectionDragInitialNodes = this.selectedEventIds
        .map(id => ({
          id,
          x: this.svgGraph?.[id]?.x ?? 0,
          y: this.svgGraph?.[id]?.y ?? 0
        }))

      // Find input and output anchor nodes for switching logic
      const { inputAnchor, outputAnchor } = dragFunctions.getMultiSelectionAnchors(this.selectedEventIds, this.svgGraph)
      if (inputAnchor) {
        this.multiSelectionInputAnchorId = inputAnchor.id
        this.multiSelectionInputSlotX = positionHelpers.getSlotXFromId(inputAnchor.id, this.svgGraph)
      }
      if (outputAnchor) {
        this.multiSelectionOutputAnchorId = outputAnchor.id
        this.multiSelectionOutputSlotX = positionHelpers.getSlotXFromId(outputAnchor.id, this.svgGraph)
      }

      document.addEventListener('mousemove', this.handleSelectionDrag)
      this.updateShowActions()
    },
    handleSelectionDrag (event) {
      if (!this.isSelectionDragging || !this.selectionDragInitialRect) {
        return
      }
      event.preventDefault()
      const deltaX = event.clientX - this.selectionDragStart.x
      const deltaY = event.clientY - this.selectionDragStart.y
      this.selectionBoundingRect = {
        x: this.selectionDragInitialRect.x + deltaX,
        y: this.selectionDragInitialRect.y + deltaY,
        width: this.selectionDragInitialRect.width,
        height: this.selectionDragInitialRect.height
      }
      this.selectionDragInitialNodes.forEach(nodeState => {
        const node = this.svgGraph?.[nodeState.id]
        if (node) {
          node.x = nodeState.x + deltaX
          node.y = nodeState.y + deltaY
        }
      })

      // Detect slot changes for switching logic (similar to single event drag)
      const selectedSet = new Set(this.selectedEventIds)

      if (this.multiSelectionInputAnchorId) {
        const inputAnchor = this.svgGraph?.[this.multiSelectionInputAnchorId]
        const outputAnchor = this.svgGraph?.[this.multiSelectionOutputAnchorId]
        if (inputAnchor) {
          const { slotX: inputSlotX } = positionHelpers.getSlotsFromNode(inputAnchor, this.svgGraph)
          const { slotX: outputSlotX } = positionHelpers.getSlotsFromNode(outputAnchor, this.svgGraph)

          if (inputSlotX === this.multiSelectionInputSlotX) {
            return
          }

          // Dragging LEFT by one slot - check if should switch with external parent of input anchor
          if (inputSlotX === this.multiSelectionInputSlotX - 1) {
            // If the outpuGroup is different from the beforeGroup, adjust the group size
            const inputGroup = positionHelpers.getGroupFromSlotX(inputSlotX, this.svgGroups)
            const beforeGroup = positionHelpers.getGroupFromSlotX(this.multiSelectionInputSlotX, this.svgGroups)

            if (inputGroup && inputGroup !== beforeGroup) {
              this.adjustGroupSizeForMultiSelection('left', inputGroup, inputAnchor, outputAnchor, selectedSet)
              if (inputAnchor.groupId) {
                outputAnchor.childIds?.forEach(childId => {
                  const child = this.svgGraph?.[childId]
                  if (!child) return
                  const { slotX: childSlotX } = positionHelpers.getSlotsFromNode(child, this.svgGraph)
                  const childGroup = positionHelpers.getGroupFromSlotX(childSlotX, this.svgGroups)
                  if (inputAnchor.groupId === childGroup.id) {
                    child.groupId = inputAnchor.groupId
                  }
                })
              }
              inputAnchor.groupId = null
              this.alignBranchesPreservingSelection(selectedSet)
            }

            // Check if the switching the position of the external parent is needed, if so, do it
            const externalParentId = inputAnchor.parents.find(p => p !== 'start' && !selectedSet.has(p))
            if (externalParentId) {
              const externalParent = this.svgGraph?.[externalParentId]
              if (externalParent) {
                const { slotX: externalParentSlotX } = positionHelpers.getSlotsFromNode(externalParent, this.svgGraph)
                const externalParentGroup = positionHelpers.getGroupFromSlotX(externalParentSlotX, this.svgGroups)
                const inputGroup = positionHelpers.getGroupFromSlotX(inputSlotX, this.svgGroups)

                if (externalParentGroup === inputGroup && externalParentSlotX === inputSlotX) {
                  dragFunctions.switchSelectionWithParent(this.multiSelectionInputAnchorId, this.multiSelectionOutputAnchorId, selectedSet, this.svgGraph, this.svgGroups)
                }
              }
            }

            this.multiSelectionInputSlotX -= 1
            this.multiSelectionOutputSlotX -= 1
          }

          // Dragging RIGHT by one slot - check if should switch with external child of output anchor
          if (outputSlotX === this.multiSelectionOutputSlotX + 1) {
            // If the outpuGroup is different from the beforeGroup, adjust the group size
            const outputGroup = positionHelpers.getGroupFromSlotX(outputSlotX, this.svgGroups)
            const beforeGroup = positionHelpers.getGroupFromSlotX(this.multiSelectionOutputSlotX, this.svgGroups)

            if (outputGroup !== beforeGroup) {
              this.adjustGroupSizeForMultiSelection('right', outputGroup, inputAnchor, outputAnchor, selectedSet)
              inputAnchor.groupId = outputGroup.id
              outputAnchor.childIds?.forEach(childId => {
                const child = this.svgGraph?.[childId]
                if (child && child.groupId === outputGroup.id) {
                  child.groupId = null
                }
              })
              this.alignBranchesPreservingSelection(selectedSet)
            }

            // Check if the switching the position of the external child is needed, if so, do it
            const externalChildId = outputAnchor.childIds?.find(childId => !selectedSet.has(childId))
            if (externalChildId) {
              const externalChild = this.svgGraph?.[externalChildId]
              if (externalChild) {
                const { slotX: externalChildSlotX } = positionHelpers.getSlotsFromNode(externalChild, this.svgGraph)
                const externalChildGroup = positionHelpers.getGroupFromSlotX(externalChildSlotX, this.svgGroups)

                if (externalChildGroup === outputGroup && externalChildSlotX === outputSlotX) {
                  dragFunctions.switchSelectionWithChild(this.multiSelectionInputAnchorId, this.multiSelectionOutputAnchorId, selectedSet, this.svgGraph, this.svgGroups)
                }
              }
            }

            this.multiSelectionInputSlotX += 1
            this.multiSelectionOutputSlotX += 1
          }
        }
      }
    },
    stopSelectionDrag (commitUpdate = true) {
      if (!this.isSelectionDragging) {
        return
      }
      document.removeEventListener('mousemove', this.handleSelectionDrag)
      this.enableNormalScroll()
      const deltaX = (this.selectionBoundingRect?.x ?? 0) - (this.selectionDragInitialRect?.x ?? 0)
      const deltaY = (this.selectionBoundingRect?.y ?? 0) - (this.selectionDragInitialRect?.y ?? 0)
      const moved = Math.abs(deltaX) > 1 || Math.abs(deltaY) > 1
      this.isSelectionDragging = false
      this.dragging = false
      this.selectionDragStart = null
      this.selectionDragInitialRect = null

      // If we moved, set flag to prevent click handlers from clearing selection
      if (moved && commitUpdate) {
        this.justFinishedDragging = true
      }

      if (!commitUpdate || !moved) {
        this.selectionDragInitialNodes = []
        this.updateSelectionBoundingRect()
        this.updateShowActions()
        return
      }
      const updatedIds = new Set(this.selectedEventIds || [])

      // Update group assignments for selected nodes and their external children
      this.updateGroupAssignmentsAfterMultiSelectionDrag(updatedIds)

      // Use the new multi-selection snap function to handle positioning as a group
      this.snapMultiSelectionToGrid(this.selectedEventIds)

      const svgGraphClone = safeStructuredClone(this.svgGraph)
      if (this.showReleaseNo) {
        Object.keys(svgGraphClone).forEach((key) => {
          svgGraphClone[key].cards = undefined
        })
      }
      workflowApi.updateWorkflow({
        svgGraph: svgGraphClone,
        svgLanes: this.svgLanes,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'moved selection',
          target: `${updatedIds.size} item${updatedIds.size === 1 ? '' : 's'}`
        }
      })

      this.selectionDragInitialNodes = []
      this.multiSelectionSlotX = 0
      this.multiSelectionInputAnchorId = null
      this.multiSelectionOutputAnchorId = null
      this.updateSelectionBoundingRect()
      this.updateShowActions()
    },
    adjustGroupSizeForMultiSelection (direction, targetGroup, inputAnchor, outputAnchor, selectedSet) {
      // Calculate the width of selected shapes using helper function
      const multiShapeSlotSize = dragFunctions.getMultiSelectionSlotWidth(selectedSet, this.svgGraph)

      let checkSlotX = null
      let externalEventId = null

      if (direction === 'left') {
        // Consider inputAnchor
        const externalParent = this.svgGraph?.[inputAnchor.parents.find(p => p !== 'start' && !selectedSet.has(p))]

        // Calculate externalParentSlotX: either from actual parent or virtual parent at group boundary
        const externalParentSlotX = externalParent
          ? positionHelpers.getSlotXFromPoint(externalParent.x)
          : targetGroup.startSlotX - 1

        const externalParentGroup = positionHelpers.getGroupFromSlotX(externalParentSlotX, this.svgGroups)

        // Check if inputAnchor and externalParent are in the same group
        if (externalParentGroup && targetGroup.id === externalParentGroup.id) {
          // Same group: Calculate externalParentSlotX + multiShapeSlotSize
          checkSlotX = externalParentSlotX + multiShapeSlotSize
        } else {
          // Different groups: check if target group width < multiShapeSlotSize
          const groupWidth = workflowApiHelpers.getGroupWidthBySlot(targetGroup.id, this.svgGraph, this.svgGroups)
          if (groupWidth < multiShapeSlotSize) {
            checkSlotX = targetGroup.startSlotX + multiShapeSlotSize - 1
          }
        }
      } else if (direction === 'right') {
        // Consider outputAnchor
        const outputSlotX = positionHelpers.getSlotXFromId(outputAnchor.id, this.svgGraph)
        targetGroup = positionHelpers.getGroupFromSlotX(outputSlotX, this.svgGroups)

        // Get external child
        externalEventId = outputAnchor.childIds?.find(childId => !selectedSet.has(childId))

        if (externalEventId && targetGroup) {
          const externalChildSlotX = positionHelpers.getSlotXFromId(externalEventId, this.svgGraph)
          const externalChildGroup = positionHelpers.getGroupFromSlotX(externalChildSlotX, this.svgGroups)

          // Check if outputAnchor and externalChild are in the same group
          if (externalChildGroup && targetGroup.id === externalChildGroup.id) {
            // Find the most right descendant of externalChild
            const mostRightDescendant = workflowApiHelpers.getMostRightDescendantInGroup(
              externalEventId,
              externalChildGroup.id,
              this.svgGraph,
              this.svgGroups
            )

            const mostRightSlotX = mostRightDescendant
              ? positionHelpers.getSlotXFromPoint(mostRightDescendant.x)
              : externalChildSlotX

            // Calculate: mostRightSlotX + multiShapeSlotSize
            checkSlotX = mostRightSlotX + multiShapeSlotSize
          } else {
            // Different groups - check if target group width < multiShapeSlotSize
            const groupWidth = workflowApiHelpers.getGroupWidthBySlot(targetGroup.id, this.svgGraph, this.svgGroups)
            if (groupWidth < multiShapeSlotSize) {
              checkSlotX = targetGroup.startSlotX + multiShapeSlotSize - 1
            }
          }
        }
      }

      if (!targetGroup || checkSlotX === null) {
        return false
      }

      // Get sorted groups to find adjacent groups
      const sortedGroups = Object.values(this.svgGroups).sort((a, b) => a.startSlotX - b.startSlotX)
      const targetGroupIndex = sortedGroups.findIndex(g => g.id === targetGroup.id)
      const nextGroup = sortedGroups[targetGroupIndex + 1]
      const groupEndSlotX = nextGroup ? nextGroup.startSlotX - 1 : Infinity

      // Check if we need to extend the group
      if (checkSlotX > groupEndSlotX && nextGroup) {
        const shiftAmount = checkSlotX - groupEndSlotX

        // Extend the group using moveGroupRight - same as single event movement
        dragFunctions.moveGroupRight(nextGroup, this.svgGroups, shiftAmount)

        return true
      }

      return false
    },
    updateGroupAssignmentsAfterMultiSelectionDrag (selectedSet) {
      // Get input and output anchors
      const inputAnchor = this.svgGraph?.[this.multiSelectionInputAnchorId]
      const outputAnchor = this.svgGraph?.[this.multiSelectionOutputAnchorId]

      if (!inputAnchor && !outputAnchor) {
        return
      }

      // Helper function to determine if a node should have groupId
      // 1. Update inputAnchor's groupId
      if (inputAnchor) {
        inputAnchor.groupId = workflowApiHelpers.shouldHaveGroupId(inputAnchor, this.svgGraph, this.svgGroups)
      }

      // 2. Update groupIds for external children of the output anchor
      if (outputAnchor?.childIds) {
        outputAnchor.childIds.forEach(childId => {
          // Only process external children (not in selection)
          if (!selectedSet.has(childId)) {
            const childNode = this.svgGraph?.[childId]
            if (childNode && childNode.groupId == null) {
              childNode.groupId = workflowApiHelpers.shouldHaveGroupId(childNode, this.svgGraph, this.svgGroups)
            }
          }
        })
      }

      // Note: Other nodes in the selection keep their existing groupIds unchanged
    },
    alignBranchesPreservingSelection (selectedSet = null) {
      // Use provided selectedSet or default to component's selectedEventIds
      const nodesToPreserve = selectedSet || new Set(this.selectedEventIds)

      // Save positions of nodes being dragged
      const savedPositions = new Map()
      nodesToPreserve.forEach(id => {
        const node = this.svgGraph[id]
        if (node) {
          savedPositions.set(id, { x: node.x, y: node.y })
        }
      })

      // Align all branches (this correctly shifts external children by shiftAmount)
      workflowApiHelpers.alignBranches(this.svgGraph, this.svgGroups)

      // Restore dragged nodes' positions to maintain smooth drag
      nodesToPreserve.forEach(id => {
        const node = this.svgGraph[id]
        const saved = savedPositions.get(id)
        if (node && saved) {
          node.x = saved.x
          node.y = saved.y
        }
      })
    },
    delay (callback) {
      this.timeout = setTimeout(callback, 500)
    },
    resetDelay () {
      clearTimeout(this.timeout)
    },
    getCardBgClass (badge) {
      if (!badge?.color) {
        return ''
      }

      const rgb = `rgb(${badge.color.red},${badge.color.green},${badge.color.blue})`
      const name = getColorName(rgb)
      return name ? `theme-bg-${name}` : ''
    },
    getTextColour (cardType) {
      return cardTypesApi.getTextColour(cardType)
    },
    getBackgroundColour (cardType) {
      return cardTypesApi.getBackgroundColour(cardType)
    },
    connectionHasLabel (parentId, childId) {
      const xDistance = this.svgGraph[childId].x - this.svgGraph[parentId].x
      if (xDistance > 160) {
        if (this.svgGraph[parentId].type === 'bpmn:ExclusiveGateway') {
          return true
        }
      }
      return false
    },
    createPathBetweenNodes (parentId, childId) {
      const parentNode = this.svgGraph[parentId]
      const childNode = this.svgGraph[childId]
      const xDistance = childNode.x - parentNode.x
      const coordinates = svgPathHelpers.getConnectionCoordinates(parentNode, childNode)
      this.handleMarkOccupied(parentNode, childNode, coordinates)
      const breakingPoint = svgPathHelpers.getBreakingPoint(parentNode.type, childNode.type)
      if (xDistance > breakingPoint) {
        return svgPathHelpers.createForwardConnection(parentNode.type, coordinates)
      } else if (xDistance <= 0) {
        const childIsStartNode = childNode.parents.find(it => it === 'start')
        return svgPathHelpers.createBackwardConnection(childIsStartNode, coordinates)
      } else {
        return svgPathHelpers.createVerticalLine(parentId, childId, coordinates)
      }
    },
    handleMarkOccupied (parentNode, childNode, { xStart, yStart, xEnd, yEnd }) {
      if (!this.dragging && (yStart !== yEnd || xEnd < xStart)) {
        let xSlot
        if (parentNode.type === 'bpmn:ExclusiveGateway') {
          xSlot = positionHelpers.getSlotXFromPoint(parentNode.x)
        } else {
          xSlot = positionHelpers.getSlotXFromPoint(childNode.x) - 1
        }
        let ySlotStart = positionHelpers.getSlotYFromPoint(parentNode.y)
        if (xEnd < xStart) {
          ySlotStart = ySlotStart + 1
        }
        const ySlotEnd = positionHelpers.getSlotYFromPoint(childNode.y)
        if (this.$route.params.eventId) {
          if (childNode.id !== this.$route.params.eventId && parentNode.id !== this.$route.params.eventId) {
            this.markOccupied(xSlot, ySlotStart, ySlotEnd)
            if (xEnd < xStart) {
              xSlot = positionHelpers.getSlotXFromPoint(parentNode.x)
              ySlotStart = positionHelpers.getSlotYFromPoint(parentNode.y)
              this.markOccupied(xSlot, ySlotStart, ySlotEnd + 1)
            }
          }
        }
      }
    },
    async addRole () {
      const roleName = 'Lane ' + ((this.workflow.lanes?.length || 0) + 1)
      await this.addLane(
        this.svgGraph,
        this.svgLanes,
        this.svgGroups,
        this.workflow,
        roleName,
        true
      )
    },
    async addShapeAI (parentId = 'start') {
      this.$refs.generator.addShapeToDiagram(parentId)
    },
    async addCardAI (eventId, cardTypeId) {
      this.$refs.generator.addCardToDiagram(eventId, cardTypeId)
    },
    async addShape (shape = {}, options = {}) {
      this.creatingEvent = true
      try {
        // use nextTick to wait for blur events to complete first to update descriptions or labels
        await this.$nextTick()
        const selectedEventId = this.$route.params.eventId && this.svgGraph[this.$route.params.eventId]?.id
        const parentId = options.parentEventId || selectedEventId || 'start'
        if (parentId !== 'start' && this.workflow?.domainEvents?.findIndex(event => event.id === parentId) === -1) {
          // prevent double click on the same event, not necessary check but makes frontend nicer
          return
        }
        shape.parents = [parentId]
        const newEventId = await workflowApi.addEventToWorkflow(
          shape,
          options,
          this.svgLanes,
          this.svgGraph,
          this.svgGroups,
          this.workflow
        )
        await this.$router.push({ path: '/workflow/' + this.workflow.projectId + '/' + this.workflow.id + '/' + newEventId }).catch(() => {})
        scrollToEvent(newEventId)
      } finally {
        this.creatingEvent = false
      }
    },
    async addParallelEvent () {
      const selectedEventId = this.$route.params.eventId
      if (!selectedEventId || !this.svgGraph[selectedEventId]) return
      const shape = { type: 'bpmn:Task' }
      shape.parents = [selectedEventId]
      this.creatingEvent = true
      try {
        await this.$nextTick()
        const newEventId = await workflowApi.addEventToWorkflow(
          shape,
          { parentEventId: selectedEventId, parallel: true },
          this.svgLanes,
          this.svgGraph,
          this.svgGroups,
          this.workflow
        )
        await this.$router.push({ path: '/workflow/' + this.workflow.projectId + '/' + this.workflow.id + '/' + newEventId }).catch(() => {})
        scrollToEvent(newEventId)
      } finally {
        this.creatingEvent = false
      }
    },
    // Batch paste multiple shapes at once with single updateWorkflow call
    async addMultipleShapes (parentId, shapes = [], newIdTooldId = new Map(), options = {}) {
      if (!parentId || !shapes || shapes.length === 0) {
        return []
      }

      this.creatingEvent = true
      await this.$nextTick()

      // Clone svgGraph, svgLanes, svgGroups to work with
      const svgGraph = JSON.parse(JSON.stringify(this.svgGraph))
      const svgLanes = JSON.parse(JSON.stringify(this.svgLanes))
      const svgGroups = JSON.parse(JSON.stringify(this.svgGroups))

      // Handle 'start' case - no parent event exists in svgGraph
      const parentEvent = parentId !== 'start' ? svgGraph[parentId] : null
      const orphanEventId = parentEvent && parentEvent.childIds && parentEvent.childIds.length === 1 ? parentEvent.childIds[0] : null

      // Find adult shapes (no external children) and output shape
      const { adults, shapesOutput } = options

      // Collect orphan descendants and preserve their positions
      const orphanDescendantsOffsets = new Map()
      if (orphanEventId) {
        const visited = new Set() // Prevent infinite loops

        // Recursively collect all descendants with groupId filtering
        const collectDescendants = (nodeId) => {
          if (visited.has(nodeId)) return
          visited.add(nodeId)

          const node = svgGraph[nodeId]
          if (!node) return

          // Store original offset for this node
          orphanDescendantsOffsets.set(nodeId, {
            offset: node.offset,
            y: node.y
          })

          // Traverse children
          if (node.childIds && node.childIds.length > 0) {
            for (const childId of node.childIds) {
              const childNode = svgGraph[childId]
              // Only include descendants with compatible groupId
              if (childNode) {
                collectDescendants(childId)
              }
            }
          }
        }

        collectDescendants(orphanEventId)
      }

      const newEventIds = []
      const selectedEventId = this.$route.params.eventId && svgGraph[this.$route.params.eventId]?.id
      // Process each shape and add to svgGraph
      for (let i = 0; i < shapes.length; i++) {
        const { shape, shapeOptions } = shapes[i]
        shape.parents = shape.parents.map(parentId =>
          parentId === 'start' && selectedEventId ? selectedEventId : parentId
        )

        const sanitizedShape = workflowApi.sanitizeShapeForCopy(shape, shapeOptions, svgGraph, this.workflow)
        const newEventId = workflowApiHelpers.addNewShapeToSvgGraphAndAlignAllNodes(
          sanitizedShape,
          shapeOptions,
          svgLanes,
          svgGraph,
          svgGroups
        )
        newEventIds.push(newEventId)
      }

      // Handle output connections to parent event's child
      if (orphanEventId) {
        // Remove old connections to orphan event
        svgGraph[orphanEventId].parents.forEach(parentId => {
          const parentNode = svgGraph[parentId]
          if (parentNode && parentNode.childIds) {
            const index = parentNode.childIds.indexOf(orphanEventId)
            if (index !== -1) {
              parentNode.childIds.splice(index, 1)
            }
          }
        })

        // Update the connections for the orphan event to the actual new parent(s)
        svgGraph[orphanEventId].parents = shapesOutput ? [shapesOutput] : adults

        // Add orphan to the childIds of its new parent(s)
        for (const parentId of svgGraph[orphanEventId].parents) {
          const parentNode = svgGraph[parentId]
          if (parentNode) {
            if (!parentNode.childIds) {
              parentNode.childIds = []
            }
            if (!parentNode.childIds.includes(orphanEventId)) {
              parentNode.childIds.push(orphanEventId)
            }
          }
        }

        // Restore orphan descendants' vertical positions
        // This prevents them from being pushed down during shape insertion
        if (orphanDescendantsOffsets.size > 0) {
          for (const [nodeId, originalPos] of orphanDescendantsOffsets.entries()) {
            const node = svgGraph[nodeId]
            if (node && node.offset !== originalPos.offset) {
              // Only restore if the offset changed (was pushed down)
              node.offset = originalPos.offset
              node.y = originalPos.y
            }
          }
        }
      }

      // Single updateWorkflow call for all shapes
      if (newEventIds.length > 0) {
        // Count events and decisions, and collect their names
        let eventCount = 0
        let decisionCount = 0
        const names = []

        for (const eventId of newEventIds) {
          const node = svgGraph[eventId]
          if (node) {
            names.push(node.name || node.description)
            if (node.type === 'bpmn:ExclusiveGateway') {
              decisionCount++
            } else {
              eventCount++
            }
          }
        }

        // Build action description
        const parts = []
        if (eventCount > 0) {
          parts.push(`${eventCount} event${eventCount > 1 ? 's' : ''}`)
        }
        if (decisionCount > 0) {
          parts.push(`${decisionCount} decision${decisionCount > 1 ? 's' : ''}`)
        }
        const action = `pasted ${parts.join(' and ')}`

        await workflowApi.updateWorkflow({
          svgGraph,
          svgLanes,
          svgGroups,
          schemas: options?.schemas || undefined,
          workflow: this.workflow,
          updateNote: {
            action,
            target: names.join(', ')
          }
        })

        // Navigate to last pasted event
        const lastEventId = newEventIds[newEventIds.length - 1]
        this.$router.push({
          path: '/workflow/' + this.workflow.projectId + '/' + this.workflow.id + '/' + lastEventId
        }).catch(() => {}).finally(() => {
          this.creatingEvent = false
        })
        scrollToEvent(lastEventId)

        this.clearMultiSelection()
      } else {
        this.creatingEvent = false
      }

      return newEventIds
    },
    addGroup (anchorEventId) {
      let startSlotX = 0
      const groupArray = Object.values(this.svgGroups)
      if (this.hasGroups) {
        const columnCount = this.baseData.columns.length
        const lengthArray = groupArray.map(group => group.startSlotX)
        const lastGroup = Math.max(...lengthArray)
        startSlotX = Math.max(lastGroup + 1, columnCount)
      }

      const group = {
        id: randomUUID(),
        name: 'Group ' + (groupArray.length + 1),
        startSlotX,
        x: positionHelpers.getGroupXPointFromSlot(startSlotX),
        width: -1
      }
      this.svgGroups[group.id] = group

      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgLanes: this.svgLanes,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'added group',
          target: group.name
        }
      })
    },
    addIntermediateGroup (currentGroup, direction) {
      const SLOT_WIDTH = 160 // One slot is 160 pixels wide
      const groupArray = Object.values(this.svgGroups)
      const sortedGroups = this.sortedGroups
      let currentIndex = sortedGroups.findIndex(g => g.id === currentGroup.id)
      currentIndex = direction === 'after' ? currentIndex + 1 : currentIndex

      // If currentIndex is the last one (or beyond), call addGroup instead
      if (currentIndex >= sortedGroups.length) {
        return this.addGroup()
      }

      const newGroupPositions = []
      let startX = positionHelpers.getXPointFromSlot(sortedGroups[0].startSlotX)
      for (let i = 0; i < sortedGroups.length; i++) {
        const group = sortedGroups[i]
        const index = group.index >= currentIndex ? group.index + 1 : group.index
        newGroupPositions.push({ id: group.id, startX, index })
        startX += group.width
      }

      const newStartSlotX = sortedGroups[currentIndex].startSlotX
      const newGroup = {
        id: randomUUID(),
        name: 'Group ' + (groupArray.length + 1),
        startSlotX: newStartSlotX,
        x: positionHelpers.getGroupXPointFromSlot(newStartSlotX),
        width: 160,
        index: currentIndex
      }
      this.svgGroups[newGroup.id] = newGroup

      // Move all events that are at or beyond the insertion point
      // Events in groups should be aligned with their group's new position
      // Events not in groups should be moved by one slot
      const insertionX = positionHelpers.getGroupXPointFromSlot(newStartSlotX)
      for (const eventId in this.svgGraph) {
        const event = this.svgGraph[eventId]

        if (event.x + 26 >= insertionX) {
          const newGroup = newGroupPositions.find(g => event.x + 26 === g.startX || event.x === g.startX)
          if (newGroup && !event.groupId && newGroup.index > 0) {
            event.groupId = newGroup.id
          }
          event.x += SLOT_WIDTH
        }
      }

      // Shift all groups from currentIndex onwards by +1 slot
      const groupsToShift = sortedGroups.slice(currentIndex)
      for (const group of groupsToShift) {
        this.svgGroups[group.id].startSlotX += 1
        this.svgGroups[group.id].index += 1
        this.svgGroups[group.id].x = positionHelpers.getGroupXPointFromSlot(this.svgGroups[group.id].startSlotX)
      }

      this.recalculateGroupWidths()

      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgLanes: this.svgLanes,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'added group before',
          target: currentGroup.name
        }
      })
    },
    recalculateGroupWidths () {
      // Sort groups by startSlotX
      const sortedGroups = Object.values(this.svgGroups).sort((a, b) => a.startSlotX - b.startSlotX)

      // Recalculate widths based on sorted order
      for (let i = 0; i < sortedGroups.length; i++) {
        const group = sortedGroups[i]
        const groupInSvgGroups = this.svgGroups[group.id]

        if (i === sortedGroups.length - 1) {
          // Last group has width -1
          groupInSvgGroups.width = -1
        } else {
          // Calculate width based on next group's position
          const nextGroup = sortedGroups[i + 1]
          groupInSvgGroups.width = positionHelpers.getGroupXPointFromSlot(nextGroup.startSlotX) - groupInSvgGroups.x
        }
      }
    },
    updateGroupName (event, group) {
      const { textContent: newValue } = event.target

      if (newValue === this.svgGroups[group.id].name) {
        return
      }

      this.svgGroups[group.id].name = newValue
      if (event.relatedTarget?.classList?.contains('captures-description')) {
        // another button was pushed that will
        // handle the update of the group name
        return
      }
      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'changed name of group',
          target: group.name
        }
      }, true)
    },
    deleteGroup (group) {
      workflowApi.deleteGroup(group, this.svgGraph, this.svgLanes, this.svgGroups, this.workflow)
    },
    async deleteLane (id, index) {
      const name = this.svgLanes[id]?.name
      workflowApiHelpers.deleteLane(id, this.svgLanes, this.svgGraph)

      await workflowApi.updateWorkflow({
        svgLanes: this.svgLanes,
        svgGraph: this.svgGraph,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'deleted lane',
          target: name
        }
      })
    },
    isLaneEmpty (laneId) {
      if (!this.svgGraph) {
        return true
      }
      return !Object.values(this.svgGraph).some((node) => node.laneId === laneId)
    },
    enableAutoscroll (event) {
      this.clientY = event.clientY // save starting pos
      this.autoscroll = setInterval(() => {
        if (this.dragging) {
          if (this.clientY < 93 && this.selectedElement.y > 0) {
            // autoscroll up
            window.scrollBy(0, -5)
            this.scrollTop -= 5
            this.selectedElement.y -= 5
          } else {
            // autoscroll down
            const boundingRect = document.getElementById('flowchart')?.getBoundingClientRect()
            if (boundingRect) {
              const bottomOfSvg = boundingRect.y + boundingRect.height
              if ((window.innerHeight - this.clientY) < 93 && (bottomOfSvg - this.clientY) > 30) {
                window.scrollBy(0, 5)
                this.scrollTop += 5
                this.selectedElement.y += 5
              }
            }
          }
        }
      }, 20)
    },
    disableAutoscroll () {
      clearInterval(this.autoscroll)
    },
    updateAutoscrollParameters (event) {
      this.clientY = event.clientY
    },
    startDrag (event) {
      const isRect = event.target?.nodeName?.toLowerCase() === 'rect'
      const isDraggableRect = isRect && event.target?.classList?.contains('draggable') && !event.target?.classList?.contains('horizontal')
      if (event?.button === 0 && isDraggableRect) {
        return
      }
      const isLaneRect = event.target?.classList?.contains('lane-rect')
      if (event?.button === 0 && isLaneRect) {
        // Check interaction mode
        if (this.$store.state.interactionMode === 'pointer') {
          this.startSelection(event)
          return
        }
        if (this.$store.state.interactionMode === 'hand') {
          this.isDraggingCanvas = true
          this.draggingCanvasStartX = event.clientX
          return
        }
      }
      if (!this.selectedElement && !this.$store.state.isWorkflowDisabled) {
        const targetId = event.target.id.replace('p-', '')
        const isDraggable = event.target.classList.contains('draggable')
        if (isDraggable) {
          if (this.svgGraph[targetId]) {
            this.selectedElement = this.svgGraph[targetId]
            document.querySelectorAll(`[id='${this.selectedElement.id}']`).forEach(el => {
              el.removeAttribute('data-test-slot')
            })
          }

          if (this.svgGroups[targetId]) {
            this.selectedElement = this.svgGroups[targetId]
          }

          if (this.selectedElement) {
            // Clear multi-selection if dragging a single element that's not part of the multi-selection
            if (this.selectedEventIds?.length > 1 && !this.selectedEventIds.includes(this.selectedElement.id)) {
              this.clearMultiSelection()
            }

            this.dragging = true
            this.dragOffsetX = event.pageX - this.selectedElement.x
            this.dragOffsetY = event.pageY - this.selectedElement.y
            this.startDragX = event.pageX
            this.startDragY = event.pageY
            this.watchInPause = true
            this.slotX = 0
            if (this.svgGraph[this.selectedElement.id]) {
              this.slotX = positionHelpers.getSlotXFromId(this.selectedElement.id, this.svgGraph)
            } else if (this.svgGroups[this.selectedElement.id]) {
              this.slotX = this.svgGroups[this.selectedElement.id].startSlotX
            }
            this.triggerUpdateFromDrag = false
            this.currentScrollLeft = this.$refs.scrollContainer.scrollLeft
            this.disableNormalScroll()
            this.enableAutoscroll(event)
          }
        }
      }
    },
    endDrag (update = true) {
      if (this.isSelectionDragging) {
        this.stopSelectionDrag(update)
        return
      }
      // Prevent handling endDrag immediately after selection drag finishes
      if (this.justFinishedDragging) {
        this.justFinishedDragging = false
        return
      }
      this.disableAutoscroll()
      this.enableNormalScroll()
      if (this.isSelecting || this.selectionStart) {
        this.finishSelection()
      }
      if (this.selectedElement) {
        if (this.svgGraph[this.selectedElement.id]) {
          // dragging a shape
          this.snapToGrid(this.selectedElement)
          const yMovement = Math.abs(this.dragOffsetY - this.startDragY + this.selectedElement.y)
          document.querySelectorAll(`[id='${this.selectedElement.id}']`).forEach(el => {
            el.removeAttribute('data-test-slot')
          })

          const svgGraphClone = safeStructuredClone(this.svgGraph)

          if (this.showReleaseNo) {
            // In case if we have applied filter by release number (showReleaseNo), the cards in the
            // svgGraphData will be different from original value and will trigger unnecessary update, so we need
            // to set it undefined to prevent that

            Object.keys(svgGraphClone).forEach((key) => {
              svgGraphClone[key].cards = undefined
            })
          }

          if (update && (this.triggerUpdateFromDrag || yMovement > 0)) {
            workflowApi.updateWorkflow({
              svgGraph: svgGraphClone,
              svgLanes: this.svgLanes,
              svgGroups: this.svgGroups,
              workflow: this.workflow,
              updateNote: {
                action: this.selectedElement.type === 'bpmn:ExclusiveGateway' ? 'moved decision' : 'moved event',
                target: this.selectedElement.name
              }
            })
          }
        }

        if (this.svgGroups[this.selectedElement.id]) {
          // dragging a group divider
          this.svgGroups[this.selectedElement.id].startSlotX = this.slotX
          this.selectedElement.x = positionHelpers.getGroupXPointFromSlot(this.slotX)
          workflowApi.updateGroupStartingPosition(this.selectedElement.id, this.svgGraph, this.svgLanes, this.svgGroups, this.workflow)
        }
      }
      this.selectedElement = null
      this.dragging = false
      this.dragOffsetX = null
      this.dragOffsetY = null
      this.lastComittedSlotX = null
      this.triggerUpdateFromDrag = false
      this.currentScrollLeft = 0
      this.isDraggingCanvas = false
      this.DraggingCanvasStartX = 0
    },
    drag (event) {
      if (this.isSelecting) {
        this.updateSelection(event)
        return
      }
      this.clientY = event.offsetY
      this.screenY = event.screenY
      if (this.selectedElement) {
        this.updateAutoscrollParameters(event)
        this.watchInPause = false
        if (this.svgGraph[this.selectedElement.id]) {
          // Dragging an event
          this.dragging = true
          this.selectedElement.x = event.pageX - this.dragOffsetX + (this.$refs.scrollContainer.scrollLeft - this.currentScrollLeft)
          this.selectedElement.y = event.pageY - this.dragOffsetY
          const { slotX } = positionHelpers.getSlotsFromNode(this.selectedElement, this.svgGraph)
          if (
            (
              slotX === (this.slotX + 1) &&
              (
                this.hasSlotToTheRight(this.selectedElement) ||
                !this.selectedElement.childIds?.length > 0 ||
                this.hasGroups
              )
            ) ||
            (
              slotX === (this.slotX - 1) &&
              this.hasSlotToTheLeft(this.selectedElement)
            )
          ) {
            this.slotX = slotX
            this.triggerUpdateFromDrag = true // changed slot
          }
        } else if (this.svgGroups[this.selectedElement.id]) {
          // Dragging a group
          const previousGroup = this.workflow.groups[this.selectedElement.index - 1]
          const maxLeft = 50 + (previousGroup.startSlotX + 1) * 160
          const nextGroup = this.workflow.groups[this.selectedElement.index + 1]
          const maxRight = 50 + (nextGroup ? (nextGroup.startSlotX - 1) : 9999) * 160
          const pointX = event.pageX - this.dragOffsetX + (this.$refs.scrollContainer.scrollLeft - this.currentScrollLeft)
          this.selectedElement.x = Math.min(Math.max(maxLeft, pointX), maxRight)
          this.selectedElement.y = event.pageY - this.dragOffsetY
          const slotX = positionHelpers.getSlotXFromPoint(this.selectedElement.x)
          if (slotX !== this.slotX) {
            this.slotX = slotX
            this.triggerUpdateFromDrag = true // changed slot
            this.svgGroups[this.selectedElement.id].startSlotX = this.slotX
            this.svgGroups[this.selectedElement.id].width = nextGroup ? (nextGroup.startSlotX - this.slotX) * 160 : -1
            this.svgGroups[previousGroup.id].width = (this.slotX - previousGroup.startSlotX) * 160 + (previousGroup.startSlotX === 0 ? 50 : 0)
          }
        }
      }
      if (this.isDraggingCanvas) {
        this.$refs.scrollContainer.scrollLeft += this.draggingCanvasStartX - event.clientX
        this.draggingCanvasStartX = event.clientX
      }
    },
    getYPointFromSlot (slot) {
      return 30 + 140 * slot
    },
    hasSlotToTheLeft (selectedElement) {
      const isStartingPoint = (selectedElement.parents || []).findIndex(id => id === 'start') >= 0
      if (!isStartingPoint &&
        selectedElement.parents.findIndex(parentId => positionHelpers.getSlotXFromId(parentId, this.svgGraph) <= this.slotX) >= 0) {
        return true
      } else if (selectedElement.groupId && this.svgGroups[selectedElement.groupId].slotX !== 0) {
        return true
      } else {
        return false
      }
    },
    hasSlotToTheRight (selectedElement) {
      if (selectedElement.childIds?.length > 0 &&
        selectedElement.childIds.findIndex(childId => positionHelpers.getSlotXFromId(childId, this.svgGraph) >= this.slotX) >= 0) {
        return true
      } else {
        return false
      }
    },
    snapToGrid (draggedNode) {
      // snap to grid x
      const isStartingPoint = (draggedNode.parents || []).findIndex(id => id === 'start') >= 0
      if (draggedNode.groupId && this.svgGroups[draggedNode.groupId]) {
        draggedNode.x = 80 + this.svgGroups[draggedNode.groupId].startSlotX * 160
      } else if (draggedNode.parents && draggedNode.parents[0] && !isStartingPoint) {
        draggedNode.x = this.svgGraph[draggedNode.parents[0]].x + 160
      } else if (isStartingPoint) {
        draggedNode.x = 80
      } else {
        if (draggedNode.childIds && draggedNode.childIds[0]) {
          draggedNode.x = this.svgGraph[draggedNode.childIds[0]].x - 160
        }
      }

      // snap to grid y using helper function
      workflowApiHelpers.calculateNodeLaneAndOffset(draggedNode, this.svgLanes)
      this.svgGraph[draggedNode.id] = draggedNode

      // Expand lane if node is outside boundaries
      if (!workflowApiHelpers.isNodeWithinLaneBoundaries(draggedNode, this.svgLanes)) {
        this.svgLanes[draggedNode.laneId].height = (draggedNode.offset + 1) * 156
        workflowApiHelpers.updateLane(this.svgLanes[draggedNode.laneId], this.svgGraph, this.svgLanes)
      }

      workflowApiHelpers.updateVerticalPositionsIfCollision(this.svgGraph, this.svgLanes, draggedNode.id, true)
    },
    snapMultiSelectionToGrid (selectedEventIds) {
      if (!Array.isArray(selectedEventIds) || selectedEventIds.length === 0) {
        return
      }

      const selectedSet = new Set(selectedEventIds)

      // Step 1: Find the anchor node (node with external parent connection)
      let anchorNode = null
      for (const id of selectedEventIds) {
        const node = this.svgGraph?.[id]
        if (!node) continue

        if (Array.isArray(node.parents) && node.parents.length > 0) {
          for (const parentId of node.parents) {
            if (parentId !== 'start' && !selectedSet.has(parentId)) {
              // Found a node with external parent - this is our anchor
              anchorNode = node
              break
            }
          }
          if (anchorNode) break
        }
      }

      // If no external parent found, use first selected node as anchor
      if (!anchorNode) {
        const firstId = selectedEventIds[0]
        anchorNode = this.svgGraph?.[firstId]
      }

      if (!anchorNode) {
        return
      }

      // Step 2-4: Calculate snap delta using helper function
      // Note: We need to pass a COPY of anchorNode to avoid modifying it twice
      const anchorNodeCopy = { ...anchorNode }
      const { snapDeltaX, snapDeltaY, snappedX, snappedY } = workflowApiHelpers.calculateSnapDeltaForAnchor(
        anchorNodeCopy,
        this.svgGraph,
        this.svgGroups,
        this.svgLanes
      )

      // Apply the snapped position to the actual anchor node
      anchorNode.x = snappedX
      anchorNode.y = snappedY
      anchorNode.laneId = anchorNodeCopy.laneId
      anchorNode.offset = anchorNodeCopy.offset
      this.svgGraph[anchorNode.id] = anchorNode

      // Expand lane if node is outside boundaries
      if (!workflowApiHelpers.isNodeWithinLaneBoundaries(anchorNode, this.svgLanes)) {
        this.svgLanes[anchorNode.laneId].height = (anchorNode.offset + 1) * 156
        workflowApiHelpers.updateLane(this.svgLanes[anchorNode.laneId], this.svgGraph, this.svgLanes)
      }

      // Step 5: Apply same delta to all other selected nodes (preserving relative positions)
      selectedEventIds.forEach(id => {
        if (id !== anchorNode.id) {
          const node = this.svgGraph?.[id]
          if (node) {
            // Move by same amount as anchor - PRESERVES INTERNAL CONNECTIONS
            node.x += snapDeltaX
            node.y += snapDeltaY

            // Update lane info based on new Y position using helper function
            workflowApiHelpers.calculateNodeLaneAndOffset(node, this.svgLanes)
            this.svgGraph[node.id] = node

            // Expand lane if node is outside boundaries
            if (!workflowApiHelpers.isNodeWithinLaneBoundaries(node, this.svgLanes)) {
              this.svgLanes[node.laneId].height = (node.offset + 1) * 156
              workflowApiHelpers.updateLane(this.svgLanes[node.laneId], this.svgGraph, this.svgLanes)
            }
          }
        }
      })

      // Step 6: Handle collisions with external (non-selected) shapes only
      // Check each selected node for collisions with non-selected nodes
      selectedEventIds.forEach(id => {
        const node = this.svgGraph?.[id]
        if (node) {
          // Check if this node collides with any non-selected node
          let targetNode = null
          for (const otherId in this.svgGraph) {
            if (!selectedSet.has(otherId)) {
              const otherNode = this.svgGraph[otherId]
              if (otherNode.placeholder !== true &&
                otherNode.x === node.x &&
                otherNode.y === node.y &&
                otherNode.laneId === node.laneId) {
                targetNode = otherNode
                break
              }
            }
          }
          // If collision found with external shape, move the external shape down
          if (targetNode) {
            workflowApiHelpers.updateVerticalPositionsIfCollision(
              this.svgGraph,
              this.svgLanes,
              targetNode.id, // Move the OTHER shape, not the selected one
              true
            )
          }
        }
      })
    },
    disableNormalScroll () {
      // Get the current page scroll position
      this.scrollTop = window.pageYOffset || document.documentElement.scrollTop
      this.scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

      // if any scroll is attempted, set this to the previous value
      window.onscroll = () => {
        window.scrollTo(this.scrollLeft, this.scrollTop)
      }
    },
    enableNormalScroll () {
      // remove the eventhandler by assigning an empty function
      window.onscroll = function () { }
    },
    computeSwimlanePath (x, y, width, height) {
      y = y - 0.5
      return `M${x},${y}h${width}`
    },
    getActionsMenuXPosition () {
      const minX = 33
      if (this.selectionBoundingRect) {
        // Center the menu at the bottom of the bounding rect
        // Using transform: translateX(-50%) to center, so we position at the center X
        const rectCenterX = this.selectionBoundingRect.x + (this.selectionBoundingRect.width / 2)
        return Math.max(minX, rectCenterX)
      }
      const node = this.primarySelectedEvent
      if (!node) {
        return minX
      }
      const value = node.x - 117
      return Math.max(minX, value)
    },
    constrainActionsMenuYPosition () {
      let offset = 105
      if (this.hasGroups) {
        offset = 129
      }
      if (this.selectionBoundingRect) {
        // Position at the bottom of the bounding rect with some padding
        return this.selectionBoundingRect.y + this.selectionBoundingRect.height + 8
      }
      const node = this.primarySelectedEvent
      if (!node) {
        return offset
      }
      return node.y + offset
    },
    closeTransformOptions () {
      return { handler: this.hideTransformOptions, include: this.getBodyIfSubworkflowDialogIsOpen }
    },
    getBodyIfSubworkflowDialogIsOpen () {
      if (document.querySelector('.selectSubworkflowDialog')) {
        return [document.querySelector('body')]
      } else {
        return []
      }
    },
    hideTransformOptions () {
      this.showTransformOptions = false
    },
    handleScroll (ev) {
      const scrollLeft = this.$refs.scrollContainer.scrollLeft
      this.setBpmnScrollLeft(scrollLeft)
    },
    handleWindowResize () {
      const svgContainerWidth = window.innerWidth - 40
      this.setSvgContainerWidth(svgContainerWidth)
    },
    shouldSwitchWithChild (newSlotX, oldSlotX, { childNode }, preserveOrder) {
      return (
        childNode &&
        newSlotX > oldSlotX &&
        positionHelpers.getSlotXFromId(childNode.id, this.svgGraph) === newSlotX &&
        !preserveOrder
      )
    },
    shouldSwitchWithParent (newSlotX, oldSlotX, { parentNode }, preserveOrder) {
      return (
        parentNode &&
        newSlotX < oldSlotX &&
        newSlotX === positionHelpers.getSlotXFromId(parentNode.id, this.svgGraph) &&
        !preserveOrder
      )
    },
    // set the attribute of a DOM element
    // for testing purposes
    setDOMAttribute (id, attribute, value) {
      document.getElementById(id).setAttribute(attribute, value)
    },
    markOccupied (xSlot, ySlotStart, ySlotEnd) {
      const loopStart = Math.min(ySlotStart, ySlotEnd)
      const loopEnd = Math.max(ySlotStart, ySlotEnd)
      for (let y = loopStart; y <= loopEnd; y++) {
        const key = `${xSlot},${y}`
        this.occupiedSlotsVerticalLines[key] = true
      }
    },
    isColliding (xSlot, ySlotStart, ySlotEnd, backwardsConnection) {
      let occupiedCounter = 0
      const loopStart = Math.min(ySlotStart, ySlotEnd)
      const loopEnd = Math.max(ySlotStart, ySlotEnd)
      for (let y = loopStart; y <= loopEnd; y++) {
        const key = `${xSlot},${y}`
        if (this.occupiedSlotsVerticalLines?.[key]) {
          occupiedCounter++
          if (occupiedCounter > 1 || (backwardsConnection && occupiedCounter > 0)) {
            return true
          }
        }
      }
      return false
    }
  },
  mounted () {
    this.latestEventId = this.$route.params.eventId
    this.handleWindowResize()
    window.addEventListener('resize', this.handleWindowResize)
    document.addEventListener('mouseup', this.endDrag)
    this.$refs.scrollContainer.addEventListener('scroll', this.handleScroll)
    const svg = document.getElementById('flowchart')
    if (svg) {
      svg.addEventListener('mousedown', this.startDrag)
      svg.addEventListener('mousemove', this.drag)
      svg.addEventListener('mouseup', this.endDrag)
    }
    EventBus.$on('enable-connection-targets', val => {
      this.enableConnectionTargets = val
      if (val) {
        if (!this.onEscPressed) {
          this.onEscPressed = event => {
            EventBus.$emit('enable-connection-targets', false)
          }
          window.addEventListener('keyup', this.onEscPressed)
        }
      } else {
        if (this.onEscPressed) {
          window.removeEventListener('keyup', this.onEscPressed)
          delete this.onEscPressed
        }
      }
    })
    EventBus.$on('reset-diagram', () => {
      this.resetDiagram()
    })
    EventBus.$on('open-card-comments', item => {
      if (item.eventId) {
        this.inspectEvent(item.eventId, 'comments')
      }
    })
    this.svgLanes = Object.assign({}, this.svgLanesData)
    this.resetDiagram()

    this.cardsByEventId = this.computeCardsByEventId()

    this.$nextTick(() => {
      Object.values(this.svgGroups).forEach(group =>
        this.updateFontSizeDynamically(this.$refs[`groupName-${group.id}`][0], true)
      )
    })
    this.updateShowActions()
  },

  beforeUnmount () {
    EventBus.$off('enable-connection-targets')
    EventBus.$off('reset-diagram')
    EventBus.$off('open-card-comments')
    window.removeEventListener('resize', this.handleWindowResize)
    document.removeEventListener('mouseup', this.endDrag)
    this.$refs.scrollContainer?.removeEventListener('scroll', this.handleScroll)
    const svg = document.getElementById('flowchart')
    if (svg) {
      svg.removeEventListener('mousedown', this.startDrag)
      svg.removeEventListener('mousemove', this.drag)
      svg.removeEventListener('mouseup', this.endDrag)
    }
    if (this.onEscPressed) {
      window.removeEventListener('keyup', this.onEscPressed)
    }
    document.removeEventListener('mousemove', this.handleSelectionDrag)
  },

  watch: {
    svgGraphData: {
      handler: function (newVal, oldVal) {
        this.occupiedSlotsVerticalLines = {}
        this.resetDiagram()
      },
      deep: true
    },
    svgLanesData: function (newLanesData, oldLanesData) {
      this.svgLanes = Object.assign({}, this.svgLanesData)
    },
    svgGroupsData: {
      handler: function (_, oldGroupsData) {
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            Object.values(this.svgGroups).forEach(group => {
              const element = this.$refs[`groupName-${group.id}`]?.[0]
              if (element) {
                const oldGroup = oldGroupsData?.[group.id]
                if (!oldGroup || oldGroup.width !== group.width) {
                  this.updateFontSizeDynamically(element, true)
                }
              }
            })
          })
        })
      },
      deep: true
    },
    slotX: function (newSlotX, oldSlotX) {
      if (!this.watchInPause && this.svgGraph[this.selectedElement?.id]) {
        document.querySelectorAll(`[id='${this.selectedElement.id}']`).forEach(el => {
          el.setAttribute('data-test-slot', newSlotX)
        })
        const involvedNodes = dragFunctions.getSurroundingNodes(this.selectedElement.id, this.svgGraph, this.svgGroups)
        let { switchedPlaces, preserveOrder } = dragFunctions.handleGroupSwitch(involvedNodes, newSlotX, oldSlotX, this.slotToGroupMap, this.svgGraph, this.svgGroups)
        if (this.shouldSwitchWithChild(newSlotX, oldSlotX, involvedNodes, preserveOrder)) {
          dragFunctions.switchCurrentNodeWithChild(involvedNodes, this.svgGraph, this.svgGroups) // dragging right
          switchedPlaces = true
        } else if (this.shouldSwitchWithParent(newSlotX, oldSlotX, involvedNodes, preserveOrder)) {
          dragFunctions.switchCurrentNodeWithParent(involvedNodes, this.svgGraph, this.svgGroups) // dragging left
          switchedPlaces = true
        }
        if (switchedPlaces) {
          workflowApiHelpers.refreshDiagramPartially(this.svgGraph, this.svgLanes, this.svgGroups, involvedNodes, this.selectedElement)
        }
      }
    },
    dragging (newVal) {
      if (newVal) {
        this.showActions = false
        return
      }
      this.updateShowActions()
    },
    disabled (newVal) {
      if (newVal) {
        const svg = document.getElementById('flowchart')
        svg.removeEventListener('mousedown', this.startDrag)
        svg.removeEventListener('mousemove', this.drag)
        svg.removeEventListener('mouseup', this.endDrag)
        // Clear any multi-selection when workflow becomes locked
        this.clearMultiSelection()
        // Switch to hand tool when workflow is locked
        this.$store.commit('setInteractionMode', 'hand')
      } else {
        const svg = document.getElementById('flowchart')
        svg.addEventListener('mousedown', this.startDrag)
        svg.addEventListener('mousemove', this.drag)
        svg.addEventListener('mouseup', this.endDrag)
      }
    },
    $route: {
      handler: function () {
        this.occupiedSlotsVerticalLines = {}
        this.showTransformOptions = false
        if (this.$route.params.eventId) {
          this.latestEventId = this.$route.params.eventId
          if (!this.hasClosedSidePanel) {
            // Only open until user has manually closed it once
            this.$store.commit('showSidePanel', true)
          }
          if (!this.selectionEvents.length) {
            this.selectedEventIds = [this.$route.params.eventId]
          }
          this.updateSelectionBoundingRect()
        } else {
          this.latestEventId = null
          if (!this.hasClosedSidePanel && ['cards', 'fields', 'fileUpload'].includes(this.sidePanelTab)) {
            this.$store.commit('showSidePanel', false)
          }
          if (!this.selectionEvents.length) {
            this.selectedEventIds = []
          }
          this.updateSelectionBoundingRect()
        }
        this.updateShowActions()
      },
      deep: true
    },
    bpmnScrollLeft (newVal) {
      // receive scroll event from the UserStoryMapping component
      this.$refs.scrollContainer.scrollLeft = newVal
    },
    '$store.state.interactionMode': function (newMode, oldMode) {
      // Clear selection when switching between hand and pointer tools
      if (newMode !== oldMode) {
        this.clearMultiSelection()
      }
    },
    selectedEventIds: {
      handler: function (newIds) {
        this.updateSelectionBoundingRect()
        this.updateShowActions()
        // Emit to parent for reactive prop binding
        this.$emit('update-selected-event-ids', newIds)
      },
      deep: true
    },
    events: {
      handler: function (newVal) {
        this.cardsByEventId = this.computeCardsByEventId()
      },
      deep: true
    }
  }
}

</script>

<style scoped>
svg {
  fill: #ffffff;
}

.ml-05 {
  margin-left: 2px;
}

.canvas-container {
  min-height: 140px;
}

.scroll-container {
  position: relative;
  overflow-x: scroll;
  scrollbar-width: none;
  min-height: 140px;
}

.selection-box {
  position: absolute;
  border: 1px dashed #2e9ce6;
  background: rgba(46, 156, 230, 0.15);
  pointer-events: none;
  z-index: 10;
}

.selection-bounds {
  position: absolute;
  border: 1px solid #2e9ce6;
  background: rgba(46, 156, 230, 0.06);
  pointer-events: none;
  z-index: 11;
}

.selection-bounds-invalid {
  border: 1px dashed #e63946;
  background: rgba(230, 57, 70, 0.15);
  cursor: default;
  pointer-events: none;
}

.selection-error-message {
  position: fixed;
  background: #e63946;
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  pointer-events: none;
  z-index: 99999;
}

.selection-error-message::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #e63946;
}

.scrollable-container::-webkit-scrollbar {
  display: none;
}

.scroll-container::-webkit-scrollbar-thumb {
  height: 8px;
  border: 1px solid var(--background);
  border-radius: 5px;
  background-color: #aaa;
  background-origin: content-box;
}

.group {
  background: var(--diagram-group-header);
  height: 24px;
  padding: 4px 8px 3px 8px;
  text-align: center;
}

.group-first {
  border-top-left-radius: 4px;
}

.group-last {
  border-top-right-radius: 4px;
}

.show-side-panel-button {
  position: fixed;
  top: 22px;
  right: -2px;
  z-index: 9;
}

.actions-enter-active,
.actions-leave-active {
  transition: opacity 0.15s ease;
}

.actions-enter-from,
.actions-leave-to {
  opacity: 0;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.2s ease-in-out;
}

.slide-in-enter-from {
  transform: translateX(370px);
}

.slide-in-enter-to {
  transform: translateX(0);
}

.slide-in-leave-from {
  transform: translateX(0);
}

.slide-in-leave-to {
  transform: translateX(370px);
}

.v-tooltip__content {
  background: var(--light-1);
  color: var(--text-dark);
  border-radius: 10px;
}

.v-tooltip__content code {
  background: var(--color-primary);
  color: var(--light-1);
}

.card-badge {
  cursor: pointer;
  width: 15px;
  height: 15px;
  text-align: center;
  line-height: 16px;
  border-radius: 2px !important;
  flex-shrink: 0;
}

.card-more-badge {
  cursor: pointer;
  text-align: center;
  border-radius: 2px !important;
  background-color: #363A40;
  color: #FCFCFC;
  flex-grow: 1;
  margin-left: 2px;
}

.card-badge-number {
  display: block;
  width: 100%;
  height: 100%;
  font-size: 12px;
}

.card-section-header {
  display: flex;
  justify-content: space-between;
}

.card-section-header > *:first-child {
  flex-grow: 1;
  margin-right: 4px;
}

.card-section-header > * {
  padding: 2px 8px;
  border-radius: 6px;
}

.card-description {
  border-bottom: 1px solid #e3e3e3;
}

.bpmn-event-container:focus-within + .subprocess-icon {
  display: none;
}

.subprocess-icon {
  border-radius: 4px;
  overflow: hidden;
}

.subprocess-icon .v-icon {
  outline: none !important;
}

.lane-title-container {
  width: 100%;
  height: 100%;
}

.lane-cell {
  fill: transparent;
  stroke: var(--diagram-lane-divider);
  stroke-width: 1px;
}

.lane-cell-menu-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.lane-cell-group:hover .lane-cell-menu-wrapper {
  opacity: 1;
}

.swime-lane-path > .lane-rect:hover ~ .lane-cell-group .lane-cell-menu-wrapper {
  opacity: 1;
}

.lane-cell-menu-button {
  pointer-events: auto;
  width: 20px;
  height: 20px;
  border: none;
  background: var(--surface-elevated);
  border-radius: 4px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

.lane-cell-menu-button:disabled {
  cursor: default;
  opacity: 0.6;
}

.lane-cell-menu-button:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 1px;
}

.v-avatar {
  border-radius: 30% !important;
  position: static;
}

.btn-add-event-to {
  border-radius: 50% !important;
}

.v-menu > .v-overlay__content > .v-list {
  padding: 0px !important;
  border-radius: 8px !important;
}

.v-list-item {
  padding: 8px !important;
}

.v-list-item-title {
  font-size: 14px !important;
}
</style>

<style>
[contenteditable] {
  -webkit-user-select: text;
  user-select: text;
}

.diagram-container {
  margin-right: -40px;
  margin-bottom: 0px;
  overflow: visible;
  min-height: 176px;
}

[data-theme="dark"] .diagram-container {
  background-color: var(--light-1);
}

.sequence-arrow {
  fill: none;
  stroke: var(--diagram-stroke);
  stroke-linejoin: round;
  stroke-linecap: round;
  marker-end: url('#sequenceflow-end-white-black-1r79xjtcycpwpzz6dais2xasb');
}

.card-badge {
  cursor: pointer;
  width: 15px;
  height: 15px;
  text-align: center;
  line-height: 16px;
  border-radius: 2px !important;
  flex-shrink: 0;
}

.card-more-badge {
  cursor: pointer;
  text-align: center;
  border-radius: 2px !important;
  background-color: #363A40;
  color: #FCFCFC;
  flex-grow: 1;
  margin-left: 2px;
}

.card-badge-number {
  display: block;
  width: 100%;
  height: 100%;
  font-size: 12px;
}

.outlined {
  stroke-width: 0.5px !important;
}

path.highlighted {
  filter: drop-shadow(1px 1px 0px black);
}

[data-theme="dark"] path.highlighted {
  filter: drop-shadow(1px 1px 0px var(--diagram-stroke));
}

#flowchart path {
  stroke: var(--diagram-stroke);
  stroke-width: 1px;
  fill-opacity: 1;
}

#flowchart {
  display: block;
}

.swim-lane {
  stroke: var(--diagram-stroke);
  stroke-width: 2px;
  fill-opacity: 0;
}

/* .swim-lane:hover ~ .move-lane-down-button{
  display: block;
} */

.separator {
  height: 60px;
}

.create-first-event-button {
  padding-left: 10px;
  padding-right: 10px;
  text-transform: none !important;
}

.draggable {
  cursor: move !important;
}

.draggable.horizontal {
  cursor: ew-resize !important;
  user-select: none;
}

.notDraggable {
  cursor: default !important;
}

rect:not(.lane-rect):hover,
text:hover {
  cursor: pointer;
}

.lane-rect {
  cursor: grab;
}

.lane-rect:active {
  cursor: grabbing;
}

[data-interaction-mode="pointer"] .lane-rect {
  cursor: crosshair;
}

[data-interaction-mode="pointer"] .lane-rect:active {
  cursor: crosshair;
}

.diagram-actions {
  width: 100%;
  height: 36px;
  border-top: 1px solid #e3e3e3;
}

svg .position-fixed {
  position: fixed;
}

svg a {
  position: fixed;
}

.cursor-grab {
  cursor: grab !important;
}

.cursor-grab:active {
  cursor: grabbing !important;
}

[contenteditable="true"].single-line {
  white-space: nowrap;
  overflow: hidden;
}
[contenteditable="true"].single-line br {
  display: none;
}
[contenteditable="true"].single-line * {
  display: inline;
  white-space: nowrap;
}
</style>
